import Util from './util';

import * as log from 'loglevel';
import ShopifyStore from './shopify-store';
import {render} from 'lit-html';

import FontLoader from './font-loader';

import dataURLtoBlob from 'blueimp-canvas-to-blob';

// fabric
//LAZY import {fabric} from 'fabric';
import productPreviewTemplate from './template/product-preview.template';
import productPreviewCropTemplate from './template/product-preview-crop.template';
import ConditionsEngine from './conditions-engine';
import RenderUtil from './renderer/render-util';
import RendererRegistry from './renderer/renderer-registry';
import ResourceLoader from './resource-loader';
import {ThemeConfig} from './const';
import {designSvg, withUploadExport} from './export-url';

// Layer actions tooltip
import tippy from 'tippy.js';
import 'tippy.js/dist/tippy.css';

// NEW: center snap while dragging (object:moving handler - enabled per area layer via params.snap)
const SNAP_THRESHOLD = 8; // screen px - converted to canvas px via the current zoom

class ProductPreview {

    constructor(config, previewConfig, productForm, conditionsEngine, options) {
        // Default settings
        let defaults = {
            api: 'https://api.cloudlift.app',
            assets: 'https://assets.cloudlift.app',
            queryParams: {},
            selector: '.cl-preview',
            selectorThumbnail: '', // optional selector of theme gallery thumbnail (used to auto select the live preview in the gallery when options change)
            position: '', // optional element position used with https://developer.mozilla.org/en-US/docs/Web/API/Element/insertAdjacentElement
            classNamePrefix: 'cl-po',
            product: {},
            variantId: null,
            editorMode: false,
            editLayer: RenderUtil.editLayer, // current layer that can be edited, default '_' to exclude all,
            cropMode: false,
            cropLayer: '',
            sticky: false,
            stickyMobile: 'overlay', // UX of mobile sticky
            stickyMobileSize: 25, // % height of mobile sticky
            stickyMode: false,
            maxHeight: 0, // NEW: support max height when scaling (used on preview popup),
            zoom: { // NEW: support zoom on hover
                enabled: false,
                factor: 2.0, // default zoom factor = retina 2x
                mobile: true // default also zoom on mobile
            },
            zoomMode: false // set when currently zooming
        };

        this.config = config;

        // NEW: dedicated previewConfig to support multi preview
        this.previewConfig = previewConfig;

        // NEW: multi view this flag is set to false when view is not visible
        this.active = true;

        // Merge options into defaults
        this.settings = Util.extend(defaults, options || {});

        // layer visible states {name: true/false}  ( mainly used to sync in editor )
        this.visible = {};
        this.visibleEditor = {}; // visible overwrite in editorMode

        this._pendingRestoreState = null;
        this._movedLayerOrder = null;
        /**
         * get shopify store ref to watch for variant changes
         * @type {ShopifyStore}
         */
        this.shopifyStore = ShopifyStore.getInstance();

        this._productForm = productForm;

        this.conditionsEngine = conditionsEngine;

        // Layer actions tooltip state
        this._layerActionsTippy = null;
        this._currentActionsLayer = null;

        // check if selector is already an element
        if(this.settings.selector !== null){ // null check to avoid missing container
            if(Util.isElement(this.settings.selector)){
                this.element = this.settings.selector;
            } else {
                this.element = document.querySelector(this.settings.selector);
            }
        }

        if (this.element != null) {

            log.info('--> new product-preview: ', this.element);

            // resources loaded async
            this.resources = [
                this.settings.assets + '/static/lib/fabricjs/fabric.min.js'
            ];


            /* load resources if not already ( this.ready can be checked from outside with a promise.then ) */
            this.ready = ResourceLoader.loadOnce(this.resources, 'product-preview',  {async: false })
                // wait for resources to be ready before init
                // IMPORTANT: only resolve ready after init
                .then(() =>  this._init());

            /* DEBUG: import fabricjs load:
            this.ready = Promise.resolve().then(() =>  this._init());*/

        } else {
            log.warn('no elements with selector found: ', this.settings.selector);
        }

    }

    destroy(){
        // Cleanup layer actions tooltip
        this._destroyLayerActionsTooltip();
        // remove wrapper
        this.element.removeChild(this._wrapper);
    }

    render(){
        render(productPreviewTemplate(this.settings.product, { uuid: this.config.uuid }), this._wrapper);
    }

    _renderContext(){
        return {
            config: this.previewConfig,
            options: this.config.options,
            product: this.settings.product,
            assets: this.settings.assets, // used for lazy loading resources (QR code layer)
            productForm: this._productForm, // used to trigger options render (currently used for text editor)
            variantId: this.settings.variantId,
            editorMode: this.settings.editorMode,
            editLayer: this.settings.editLayer,
            cropMode: this.settings.cropMode, // flag if the preview is in crop mode
            cropLayer: this.settings.cropLayer, // layer that is currently cropped
            mask: false, // flag if a mask is active on the canvas (required to auto remove mask if layer is not active anymore),
            cdnSize: ThemeConfig.previewCdnSize, // NEW: map flag to use cdn for Overlay and Product layer
            areas: {} // NEW: per render registry of visible area layers { key: layer } - registered in _renderLayer, read in _materializeArea
        };
    }

    renderPreview(){
        // INFO: internal queue to always wait for the last render to finish before a new render is started
        if(this._renderQueue == null){
            this._renderQueue = Promise.resolve();
        }

        // NEW: always load fonts first
        this._renderQueue = this._renderQueue
            .then(() => this._loadUsedFonts())
            .then(() => this._renderCanvas());

        return this._renderQueue;
    }

    _renderCanvas() {
        let level = log.getLevel();

        if (level <= log.levels.DEBUG) {
            console.time('====> RENDER PREVIEW '+ this.previewConfig.position);
        }

        // Hide layer actions tooltip before re-rendering
        this._hideLayerActionsTooltip();

        // render context
        let context = this._renderContext();

        this._applyRestoreState();

        // we store the layer name instead of object reference ( to support full object reset & re-select - mainly happens in editorMode )
        let selectedLayer = null;

        if(!this.static){
            let selectedObject = this._canvas.getActiveObject();
            this._canvas.discardActiveObject();
            if(selectedObject){
                selectedLayer = selectedObject._layer;
            }
        }

        // full canvas rotation group - reset
        if(this._rotateGroup){
            // IMPORTANT: manually remove all objects from group to reset rotation
            this._rotateGroup.forEachObject((obj) => {
                this._rotateGroup.remove(obj);
            });
        }

        // clear all the rendered elements ( solves z-index and nested conditions )
        this._canvas.clear();

        // NEW: interactive area guides are recreated hidden by this render - reset the tracker to match
        // (a gesture never spans a render, so hidden is the correct post-render state; without the reset
        // the next _syncAreaGuide would early-exit on the persistent area layer reference)
        this._activeAreaGuide = null;

        // render all layers
        let renderPromise = this._renderLayers(this.previewConfig.layers, context);

        return renderPromise.then(() => {
            // NEW: the visible area layers of the last render ( export entries with a print area read them )
            this.areas = context.areas;

            this._applyMovedLayerOrder();

            // check if a clipPath is still set without an active mask
            if(this._canvas.clipPath && !context.mask){
                delete this._canvas.clipPath; // reset
            }

            // re-select previously selected layer ( must be called before renderAll )
            if(selectedLayer){
                this.selectLayer(selectedLayer.name, false); // avoid render call we call it below
            } else if(RenderUtil.hasEditLayer(this.settings.editorMode, this.settings.editLayer)){
                this.selectLayer(this.settings.editLayer, false); // editor selected layer (that was de-selected manually or was not yet set as .selectable e.g autoPos)
            }

            // field to rotate all objects on the canvas
            if(context.canvasRotate !== undefined){
                // always create a new group - canvas objects could have changed
                this._rotateGroup = new fabric.Group(this._canvas.getObjects())
                this._rotateGroup.rotate(context.canvasRotate); // only 90 degree rotation supported
                this._rotateGroup.setCoords();
            }

            // render all updates
            this._canvas.renderAll();

            if (level <= log.levels.DEBUG) {
                console.timeEnd('====> RENDER PREVIEW ' + this.previewConfig.position);
            }

            // find layers marked as responsive to update the height
            this._responsiveCanvasHeight();

            // auto select thumbnail of theme gallery to show the updated preview (placed here instead of option:changed/variant_changed to always select the live preview on page load)
            this._syncThemeGallery();

            // event ( used on server to sync visible states )
            this._triggerEvent('preview:rendered', this);

            // return context to allow checking for changes (used for auto crop)
            return context;
        });
    }

    downloadBlob(blob, filename) {
        // Create an object URL for the blob object
        const url = URL.createObjectURL(blob);

        // Create a new anchor element
        const a = document.createElement('a');

        // Especially, if the anchor element will be attached to the DOM
        a.href = url;
        a.download = filename || 'download';

        // Click handler that releases the object URL after the element has been clicked
        // This is required for one-off downloads of the blob content
        const clickHandler = function(e) {
            setTimeout(() => {
                URL.revokeObjectURL(url);
                this.removeEventListener('click', clickHandler);
            }, 150);
        };

        // Add the click event listener on the anchor element
        // Comment out this line if you don't want a one-off download of the blob content
        a.addEventListener('click', clickHandler, false);

        a.click();

        // Return the anchor element
        // Useful if you want a reference to the element
        // in order to attach it to the DOM or use it in some other way
        return a;
    }

    export(dimension, crop, blob, paramExclude, format = 'jpeg', quality = 0.8, skipEmpty = true){

        if(this._canvas){

            let level = log.getLevel();

            if (level <= log.levels.DEBUG) {
                console.time('====> EXPORT PREVIEW');
            }

            // hide all objects that are excluded from export
            let hasExcluded = false;
            let visibleLayers = 0;

            // NEW: support dynamic function to exclude layers (used on ai generator to avoid including the generated ai image itself)
            const isExcluded = typeof paramExclude === 'function' ? paramExclude : (obj) => obj._layer.params[paramExclude] === true;

            this._canvas.forEachObject((obj) => {

                if(obj._layer){

                    if(obj.visible){

                        // marked as exclude from export
                        if(isExcluded(obj)){
                            obj.visible = false;
                            obj._visible = true; // mark for restore

                            hasExcluded = true;
                        }
                        // exclude product from visible count (no need to upload just the product image)
                        // NEW: also exclude area guide boxes (display, docs/layer-area.md section 9) -
                        // a guide alone is no customer design, skipEmpty must still skip the export
                        else if(obj._layer.type !== 'product' && obj._layer.type !== 'area'){
                            visibleLayers++;
                        }
                    }
                    // NEW: exportOnly flag to hide the layer from the preview but still export it
                    else if(obj._layer.params.exportOnly){
                        obj.visible = obj._layer._visibleCondition; // show on export IF conditions are met
                        obj._visible = false; // mark for restore

                        // allow combination to still exclude on preview/original
                        if(isExcluded(obj)){
                            obj.visible = false;
                        }

                        hasExcluded = true;
                    }
                }

            });

            // avoid export if no layers are visible
            if(skipEmpty && visibleLayers === 0){
                log.debug('==> no visible layers - skipping export');

                // IMPORTANT: restore visible flag (we don't render the canvas)
                if(hasExcluded){
                    // restore visible
                    this._canvas.forEachObject((obj) => {
                        if(typeof obj._visible !== 'undefined'){
                            obj.visible = obj._visible;
                            delete obj._visible;
                        }
                    });
                }

                return null;
            }

            // refresh canvas (visible flag)
            this._canvas.renderAll();

            let data;

            // Before SVG export - restore original elements for high-res export
            // When resize filter is active, _element contains the downscaled canvas.
            // We temporarily restore the original to export full resolution.
            let resizedElements = [];
            if (ThemeConfig.previewImgResize && format === 'svg') {
                this._canvas.forEachObject((obj) => {
                    if (obj.type === 'image' && obj.resizeFilter && obj._element) {
                        // Save resized element reference
                        resizedElements.push({
                            obj: obj,
                            resizedElement: obj._element
                        });
                        // Restore to original (use _filteredEl if other filters applied, else _originalElement)
                        obj._element = obj._filteredEl || obj._originalElement;
                    }
                });
            }

            // NEW: svg based export
            if(format === 'svg'){

                let svgOptions = {
                    // always use full dimensions
                    width: dimension.width,
                    height: dimension.height
                };

                if(crop){
                    // viewbox as crop
                    svgOptions.viewBox = {
                        x: crop.x,
                        y: crop.y,
                        width: crop.width,
                        height: crop.height
                    }
                }

                // layer ids on the objects ( a pipeline's cut line can follow a layer's shape ), absolute masks with their area clip,
                // curved text with its curve ( export-url.js designSvg )
                let svg = designSvg(this._canvas, svgOptions);

                /* fabric 4 renders nested clipPaths on canvas but does NOT serialize them: toClipPathSVG emits
                   clip-path="url(#undefined)" on the inner shape (clipPathId is only ever assigned in the toSVG path, never in
                   toClipPathSVG). Absolute masks ( mask-renderer.js render(), "nest the area clip" ) are written out by
                   designSvg above; any other nested clip leaves the dangling ref, which browsers ignore but which crashes
                   Batik on the server ("Cannot find the referenced element #undefined"). Strip it. */
                svg = svg.replace(/\s*clip-path="url\(#undefined\)"/g, '');

                // NEW: full-resolution upload export of an upload layer's photo for the convert service's svg render
                this._canvas.forEachObject((obj) => {
                    const layer = obj._layer;
                    // filtered images are a canvas (data url of the filtered preview) - the export cannot reproduce it
                    if(layer?.type.includes('upload') && obj._element && !obj._element.toDataURL){
                        const option = RenderUtil.getProductOption(layer.params.option, this.config.options);
                        svg = withUploadExport(svg, obj.getSrc(true), option?._controller?.field, layer._rotate, layer._fileIndex);
                    }
                });

                // NEW: hacky way to remove size params from cdn urls to get highest quality for server export
                if(ThemeConfig.previewCdnSize){
                    //  extract all urls from from xlink:href=""  from svg
                    let regex = /xlink:href="([^"]+)"/g;
                    // loop over all matches
                    let match;
                    let urls = [];
                    while (match = regex.exec(svg)) {
                        // only cdn.shopify.com have a size param
                        if(match[1].indexOf('cdn.shopify.com') !== -1){
                            urls.push(match[1]);
                        }
                    }

                    for (const url of urls) {
                        // strip cdn size from url _x300. or _300x.   NEW: added "." to prevent matching random parts in filename
                        let newUrl = url.replace(/_x\d+\./, '.').replace(/_\d+x\./, '.');
                        
                        // Ensure https: prefix for protocol-relative URLs
                        if(newUrl?.startsWith('//cdn.shopify.com')){
                            newUrl = 'https:' + newUrl;
                        }
                        
                        if(newUrl !== url){
                            log.debug('SVG export removed size from CDN url: ', url, newUrl);
                            svg = svg.replace(url, newUrl);
                        }
                    }
                }

                // find all loaded fonts stylesheets
                var fontUrls = [];

                for (let i = 0; i < document.styleSheets.length; i++) {

                    const styleSheet = document.styleSheets[i];

                    if(styleSheet.href){
                        // custom google fonts
                        if(styleSheet.href.indexOf('fonts.googleapis.com') !== -1){ // NEW: include subsets for more language support
                            fontUrls.push(styleSheet.href + '&subset=' + (window.clExportFontSubset || 'latin-ext,cyrillic-ext,greek-ext,japanese,hebrew'));
                        } // OR custom fonts in app
                        else if(styleSheet.href.indexOf(FontLoader.api) !== -1){
                            fontUrls.push(styleSheet.href);
                        }
                    }
                }

                if(fontUrls.length){
                    // insert single style tag with all font urls
                    let fontStyles = `<style type="text/css"><![CDATA[
                            ${fontUrls.map(url => `@import url("${url}");`).join('\n')}
                        ]]></style>`;

                    let defs = svg.indexOf('</defs>');

                    // insert style defs into svg
                    svg = svg.substring(0, defs) + fontStyles + svg.substring(defs);
                }

                if(blob){
                    data = new Blob([svg], {type: 'image/svg+xml'});
                } else {
                    data = svg;
                }

                // After SVG export - restore resized elements for continued preview rendering
                resizedElements.forEach(({ obj, resizedElement }) => {
                    obj._element = resizedElement;
                });
            }
            // DEFAULT: dataUrl image export
            else {

                let options = {
                    multiplier: dimension.width / this._canvas.getWidth(),
                    format: format,
                    quality: quality
                };

                // crop parts of the canvas
                if(crop){
                    // convert coordinates to be in reference to the current rendered canvas size
                    options.left = crop.x / options.multiplier;
                    options.top = crop.y / options.multiplier;
                    options.width = crop.width / options.multiplier;
                    options.height = crop.height / options.multiplier;
                }

                // docs: http://fabricjs.com/docs/fabric.Canvas.html#toDataURL
                let dataURL = this._canvas.toDataURL(options);

                if(blob){
                    /*
                       Cross Browser lib for canvas to blob:  http://blueimp.github.io/JavaScript-Canvas-to-Blob/
                       polyfills canvas.toBlob but we use the method directly to avoid the native canvas.toBlob call which is way slower )
                     */
                    //console.time('dataURLtoBlob');
                    data = dataURLtoBlob(dataURL);
                    //console.timeEnd('dataURLtoBlob');

                    /*
                      NATIVE:  is way slower for bigger outputs & its async which adds more complexity
                      let canvasEl = this._canvas.toCanvasElement(options.multiplier); // get canvas element

                      console.time('toBlob');

                      canvasEl.toBlob( (blob) => {

                          console.timeEnd('toBlob');

                          // ...
                     // }, 'image/jpeg', options.quality);
                      }, 'image/png'); */
                } else {
                    data = dataURL;
                }
            }

            if(hasExcluded){
                // restore visible
                this._canvas.forEachObject((obj) => {
                    if(typeof obj._visible !== 'undefined'){
                        obj.visible = obj._visible;
                        delete obj._visible;
                    }
                });
                this._canvas.renderAll();
            }

            if (level <= log.levels.DEBUG) {
                console.timeEnd('====> EXPORT PREVIEW');
            }

            return data;
        }

        return null;
    }

    exportLayer(layer, blob, format = 'jpeg', quality = 0.8){
        let objects = this._canvas.getObjects();

        // find the requested object layer
        for (let i = 0; i < objects.length; i++) {
            const object = objects[i];
            if(object._layer && object._layer.name === layer){

                let options = {
                    multiplier: 1, // INFO: 1 & withoutTransform=true exports the original dimensions
                    withoutTransform: true,
                    format: format,
                    quality: quality
                };

                // save clipPath ref
                let clipPath = object.clipPath;

                if(clipPath){
                    // remove clip path (did not work with absolute clipPath - we use our own absolute crop calculated below )
                    object.clipPath = null;  // no render call required

                    let cropRect = clipPath.getBoundingRect(true,true);
                    log.debug('crop rect', cropRect);

                    let boundingRect = object.getBoundingRect(true, true);
                    log.debug('bounding rect', boundingRect);

                    // scale to the original dimension
                    let multiplier = object.width / object.getScaledWidth();

                    // convert coordinates to be in reference to the current original image size
                    options.left = Math.round((cropRect.left - boundingRect.left) * multiplier);
                    options.top = Math.round((cropRect.top - boundingRect.top) * multiplier);
                    options.width = Math.round(cropRect.width * multiplier);
                    options.height = Math.round(cropRect.height * multiplier);

                    // INFO: Math.min/max to prevent rounding issues & crop outside of image dimension
                    options.left = Math.max(options.left, 0);
                    options.top = Math.max(options.top, 0);
                    options.width = Math.min(options.width, object.width - options.left);
                    options.height = Math.min(options.height, object.height - options.top);
                }

                // docs: http://fabricjs.com/docs/fabric.Object.html#toDataURL
                let dataURL = object.toDataURL(options);

                let data;

                if(blob){
                    data = dataURLtoBlob(dataURL);
                } else {
                    data = dataURL;
                }

                // reset clipPath
                if(clipPath){
                    object.clipPath = clipPath;
                }

                return data;
            }
        }

        return null;
    }

    exportLayerCoords(layer){

        let objects = this._canvas.getObjects();

        // find the requested object layer
        for (let i = 0; i < objects.length; i++) {
            const object = objects[i];
            //                                                 || hacky child layer support
            if (object._layer && (object._layer.name === layer || object._layer.name.startsWith(layer + '_'))) {

                return {
                    // round all values to integer
                    x: Math.round(object.left),
                    y: Math.round(object.top),
                    w: Math.round(object.getScaledWidth()),
                    h: Math.round(object.getScaledHeight()),
                    r: Math.round(object.angle)
                };
            }
        }

        // not found
        return null;
    }

    exportState() {
        if (!this._canvas) {
            return null;
        }

        // The saved order is the Fabric order of interactive layers only.
        const rows = [];
        const seen = new Set();
        const objects = this._canvas.getObjects();

        for (let i = 0; i < objects.length; i++) {
            const object = objects[i];
            const layer = object?._layer;
            const layerId = layer?.name;

            if (!layerId || seen.has(layerId) || !this._isDirty(layer)) {
                continue;
            }

            seen.add(layerId);

            // Crop upload layers export their image with position/rotation baked in — the URL is replaced on submit.
            // Saving their coordinates would restore stale values against a different (cropped) image, so skip the geometry.
            const isCropUpload = layer.type === 'upload' && layer.params.crop;

            rows.push({
                id: layerId,
                // state only needed if interactive changed - if null we only store the name for index order!
                state: (!isCropUpload && (layer?._interactive || layer?._interactiveScaled)) ? this._getLayerState(object) : null
            });
        }

        if (!rows.length) {
            return null;
        }

        const serializedRows = rows
            .map((row) => this._serializeRestoreRow(row))
            .filter(Boolean)
            .join(';');

        if (!serializedRows) {
            return null;
        }

        return serializedRows;
    }

    queueRestoreState(serialized) {
        this._pendingRestoreState = this._parseRestoreState(serialized);
        return this._pendingRestoreState;
    }

    _parseRestoreState(serialized) {
        const parsed = { rows: [] };

        if (typeof serialized !== 'string' || serialized.length === 0) {
            return parsed;
        }

        serialized.split(';').forEach((rowValue) => {
            if (!rowValue) {
                return;
            }

            const geometryIndex = rowValue.indexOf('@');
            const encodedId = geometryIndex === -1 ? rowValue : rowValue.substring(0, geometryIndex);
            const layerId = this._safeDecodeRestoreValue(encodedId);

            if (!layerId) {
                return;
            }

            let state = null;

            if (geometryIndex !== -1) {
                state = this._parseState(rowValue.substring(geometryIndex + 1));

                if (!state) {
                    return;
                }
            }

            parsed.rows.push({ id: layerId, state });
        });

        return parsed;
    }

    // supports delayed layer rendering as unresolved layer are kept in the pending state until they can be applied
    _applyRestoreState(parsedState = this._pendingRestoreState) {
        if (!parsedState?.rows?.length) {
            return;
        }

        const unresolvedRows = [];

        for (let i = 0; i < parsedState.rows.length; i++) {
            const row = parsedState.rows[i];
            const layer = this._findRestoreLayer(row.id);

            if (!layer) {
                unresolvedRows.push(row);
                continue;
            }

            if (row.state) {
                this._applyState(layer, row.state);
            } else {
                layer._interactiveIndex = true;
            }
        }

        if (unresolvedRows.length === 0) {
            this._movedLayerOrder = parsedState.rows.map((row) => row.id);
        }

        if (unresolvedRows.length) {
            this._pendingRestoreState = { rows: unresolvedRows };
            return;
        }

        this._pendingRestoreState = null;
    }

    _applyState(layer, geometry) {
        if (!layer || !geometry) {
            return;
        }

        const rotation = this._normalizeRotation(geometry.r);

        layer.params.x = geometry.x;
        layer.params.y = geometry.y;
        layer.params.scaleX = geometry.sx / 1000;
        layer.params.scaleY = geometry.sy / 1000;
        layer.params.angle = rotation;

        if (layer.type === 'upload' || typeof layer.params.rotate !== 'undefined' || typeof layer._rotate !== 'undefined') {
            layer.params.rotate = rotation;
            layer._rotate = rotation;
            // Uploads can rebuild their Fabric object on restore, so keep the interactive flags for one render pass.
            if (layer.type === 'upload') {
                layer._restore = true;
            }
        }

        // Text editor params override layer.params during render, so keep the editor cache in sync on restore too.
        if (layer._editor) {
            Util.merge(layer._editor, layer.params, ['x','y','scaleX','scaleY','angle']);
        }

        // Restore state now reuses the same interactive flags as live edits.
        layer._interactive = true;
        layer._interactiveScaled = true;
    }

    _findRestoreLayer(layerId, layers = this.previewConfig.layers) {
        if (!Array.isArray(layers) || !layerId) {
            return null;
        }

        for (let i = 0; i < layers.length; i++) {
            if (layers[i]?.name === layerId) {
                return layers[i];
            }
        }

        for (let i = 0; i < layers.length; i++) {
            if (Array.isArray(layers[i]?.layers)) {
                const nestedLayer = this._findRestoreLayer(layerId, layers[i].layers);

                if (nestedLayer) {
                    return nestedLayer;
                }
            }
        }

        return null;
    }

    _applyMovedLayerOrder() {
        if (!this._canvas || !Array.isArray(this._movedLayerOrder) || this._movedLayerOrder.length < 2) {
            return;
        }

        const orderIndex = new Map();

        for (let i = 0; i < this._movedLayerOrder.length; i++) {
            orderIndex.set(this._movedLayerOrder[i], i);
        }

        if (!orderIndex.size) {
            return;
        }

        const objects = this._canvas.getObjects();
        const changedSlots = [];
        const changedObjects = [];
        const seen = new Set();

        for (let i = 0; i < objects.length; i++) {
            const object = objects[i];
            const layer = object?._layer;
            const layerId = layer?.name;

            if (!layerId || seen.has(layerId) || !orderIndex.has(layerId) || !this._isIndexLayer(layer)) {
                continue;
            }

            seen.add(layerId);
            changedSlots.push(i);
            changedObjects.push(object);
        }

        if (changedObjects.length < 2) {
            return;
        }

        // Keep unchanged Fabric objects in place and only reorder the saved interactive layer subset
        changedObjects.sort((left, right) => orderIndex.get(left._layer.name) - orderIndex.get(right._layer.name));

        for (let i = 0; i < changedSlots.length; i++) {
            this._canvas.moveTo(changedObjects[i], changedSlots[i]);
        }
    }

    _isDirty(layer) {
        return !!(layer?._interactive || layer?._interactiveScaled || layer?._interactiveIndex);
    }

    _isIndexLayer(layer) {
        return !!layer?.params?.interactiveActions?.index;
    }

    _getLayerState(object) {
        const layer = object?._layer;

        if (!object || !layer) {
            return null;
        }

        return {
            x: Math.round(object.left || 0),
            y: Math.round(object.top || 0),
            sx: Math.round((object.scaleX || 1) * 1000),
            sy: Math.round((object.scaleY || 1) * 1000),
            r: this._normalizeRotation(Math.round(object.angle || 0))
        };
    }

    _serializeRestoreRow(row) {
        if (!row?.id) {
            return null;
        }

        const encodedId = encodeURIComponent(row.id);

        if (!row.state) {
            return encodedId;
        }

        return `${encodedId}@${row.state.x},${row.state.y},${row.state.sx},${row.state.sy},${row.state.r}`;
    }

    _parseState(serialized) {
        const parts = serialized.split(',');

        if (parts.length !== 5) {
            return null;
        }

        const numbers = parts.map((part) => parseInt(part, 10));

        if (numbers.some((value) => isNaN(value))) {
            return null;
        }

        return {
            x: numbers[0],
            y: numbers[1],
            sx: numbers[2],
            sy: numbers[3],
            r: this._normalizeRotation(numbers[4])
        };
    }

    _safeDecodeRestoreValue(value) {
        try {
            return decodeURIComponent(value);
        } catch (error) {
            return null;
        }
    }

    _normalizeRotation(rotation) {
        let normalized = rotation % 360;

        if (normalized < 0) {
            normalized += 360;
        }

        return normalized;
    }

    moveLayer(layer, direction) {
        if (!layer?._object || !this._canvas) {
            return false;
        }

        const objects = this._canvas.getObjects();
        const object = layer._object;
        const index = objects.indexOf(object);

        if (index === -1) {
            return false;
        }

        const step = direction === 'up'
            ? 1
            : direction === 'down'
                ? -1
                : 0;

        if (!step) {
            return false;
        }

        let targetIndex = index + step;
        let targetObject = null;

        while (targetIndex >= 0 && targetIndex < objects.length) {
            const targetLayer = objects[targetIndex]?._layer;

            // Skip non-index layers while searching for the next reorderable target.
            if (targetLayer && this._isIndexLayer(targetLayer) && objects[targetIndex] !== object) {
                targetObject = objects[targetIndex];
                break;
            }

            targetIndex += step;
        }

        if (!targetObject) {
            return false;
        }

        this._canvas.moveTo(object, targetIndex);

        // Mark both sides of the swap so exportState() persists their relative order.
        layer._interactiveIndex = true;

        if (targetObject._layer) {
            targetObject._layer._interactiveIndex = true;
        }

        this._movedLayerOrder = [];

        // Re-read the canvas after moveTo(); the pre-move array is now stale.
        const movedObjects = this._canvas.getObjects();

        for (let i = 0; i < movedObjects.length; i++) {
            const movedLayer = movedObjects[i]?._layer;
            const layerId = movedLayer?.name;

            if (!layerId || this._movedLayerOrder.includes(layerId) || !this._isDirty(movedLayer)) {
                continue;
            }

            this._movedLayerOrder.push(layerId);
        }

        this._canvas.renderAll();

        return true;
    }

    clear(){
        if(this._canvas){
            this._canvas.clear();
        }
    }

    updateConfig(previewConfig){

        let dimensionUpdate = false;
        // check if dimension changed
        if(this.previewConfig.width !== previewConfig.width || this.previewConfig.height !== previewConfig.height){
            dimensionUpdate = true;
        }

        // INFO: this resets all cached layer._object
        //  maybe support partial layer update ( only if a good requirement for it )
        this.previewConfig = previewConfig;

        // reset canvas dimensions & zoom
        if(dimensionUpdate){
            this._setDimensions();
        }

        return this.renderPreview();
    }

    selectLayer(name, render = true, layers = this.previewConfig.layers){ // default top layers for nested support

        for (let i = 0; i < layers.length; i++) {
            const layer = layers[i];

            // nested layers
            if(layer.layers && layer.layers.length > 0){
                // recursive call
                if(this.selectLayer(name, render, layer.layers)){
                    return true; // selection found return
                }
            }

            // check if layer has object
            if(layer.name === name && layer._object){

                // editorMode: layer select > show the selected object on top while selected
                if(RenderUtil.isEditLayer(layer, this.settings.editorMode, this.settings.editLayer)){
                    // INFO: we always call setActiveObject even if layer._object.selectable = false (this brings the layer to the front)
                    this._canvas.preserveObjectStacking = false;
                    // reset interactive flag (when this flag is set the params updates are not saved)
                    delete layer._interactive;
                }

                // NEW: only select if added on canvas (fixes hidden layer re-select showing controls)
                if(this._canvas.contains(layer._object)){

                    this._canvas.setActiveObject(layer._object);

                    // selection requires render
                    if(render){
                        this._canvas.renderAll();
                    }

                    return true;
                }
            }
        }

        // not found
        return false;
    }

    deSelect(render = true){
        // empty name de-select current
        this._canvas.discardActiveObject();

        // editorMode: reset object stacking set in selectLayer (required for interactive testing)
        if(this.settings.editorMode && !this._canvas.preserveObjectStacking){
            this._canvas.preserveObjectStacking = true;
        }

        if(render){
            this._canvas.renderAll();
        }
    }

    _init() {

        // create wrapper to scope by uuid
        this._wrapper = document.createElement('div');
        this._wrapper.className = this.settings.classNamePrefix + '--preview-wrapper';
        this._wrapper.setAttribute('id', this.config.uuid + '_preview');

        // IMPORTANT: for _getContainerWidth to scale to full available dimensions
        this._wrapper.style.width = '100%';

        // add wrapper to element with position support
        //https://developer.mozilla.org/en-US/docs/Web/API/Element/insertAdjacentElement
        if(this.settings.position){
            this.element.insertAdjacentElement(this.settings.position, this._wrapper);
            // case before/after -> update parent element (required for events & all this.element.querySelector methods)
            if(this.element !== this._wrapper.parentNode){
                this.element = this._wrapper.parentNode;
            }
        } else {
            // default child
            this.element.appendChild(this._wrapper);
        }

        this.render();

        // get preview element reference
        this._preview = this._wrapper.querySelector('.' + this.settings.classNamePrefix + '--preview');

        // hide before first render and resize
        this._preview.classList.add('cl-hide');

        // get canvas reference
        let canvas = this._wrapper.querySelector('.' + this.settings.classNamePrefix + '--canvas');

        // fabric canvas instance
        this.static = this._isStatic();

        // NEW: always render at least 2x ratio (1x caused blurry images)
        if(fabric.devicePixelRatio < 2){
            fabric.devicePixelRatio = 2;
        }
        // manual overwrite
        if(window.clPreviewRatio){
            fabric.devicePixelRatio = parseInt(window.clPreviewRatio);
        }

        // NEW: more precision for svg export  http://fabricjs.com/fabric-gotchas
        fabric.Object.NUM_FRACTION_DIGITS = 8;

        // NEW: disable webGL filter (fixes Colorize on PNG compression from shopify CDN)
        fabric.enableGLFiltering = false;
        // manual overwrite
        if(window.clPreviewGLFilter){
            fabric.enableGLFiltering = true;
        }

        // no interaction layer
        if(this.static){
            this._canvas = new fabric.StaticCanvas(canvas);

            // only static canvas supports zoom
            if(this.settings.zoom?.enabled){
                this._watchZoom();
            }
        } else {

            // touch devices - patch canvas for touch scroll support
            if(fabric.isTouchSupported){
                this._patchCanvasTouchScroll();
            }

            this._updateCanvasControls();

            this._canvas = new fabric.Canvas(canvas, {
                selection: false, // disable free selection rectangle (still allow individual selection)
                preserveObjectStacking: true, // keep z-index when object is selected (editorMode selection temporary overwrites this)
                allowTouchScrolling: true // flag to make scrolling work (IMPORTANT: still requires the patch)
            });

            // subscribe to events when the canvas is interactive
            this._subscribeCanvasEvents();
        }

        // allow setting on view
        //this._canvas.backgroundColor = '#cfcfcf';

        // set initial reference dimensions
        this._setDimensions()

        this.renderPreview().then(() => {

            this._watchFormChanges();

            this._watchWindowResize();

            this._watchWindowScroll();

            this._watchPreviewActive();

            // show preview
            this._preview.classList.remove('cl-hide');
        }).catch(e => {
            log.error('[live-options] failed render preview', e);
        });
    }

    _setDimensions(){
        let zoom = this._canvas.getZoom();

        // IMPORTANT: reset zoom when we set the dimensions to act as new reference
        if(zoom !== 1){
            this._canvas.setZoom(1);
        }

        let pixelRatio = fabric.devicePixelRatio; // is 1 > for retina scaling
        let pixels = this.previewConfig.width * this.previewConfig.height;
        let max = window.clPreviewMaxRetina || 3000 * 3000; // default max 3000x3000
        // for mobile devices with big reference dimensions ( Safari prints warning but keeps working, for performance reasons we disable retinaScaling )
        if(pixelRatio > 1 && pixels > max && Util.isMobile(Util.getAgent())){
            this._canvas.enableRetinaScaling = false;
        }

        this._canvas.setDimensions({ width: this.previewConfig.width, height: this.previewConfig.height });

        // resize with a new zoom factor
        if(zoom !== 1){
            this._resizeCanvas();
        }
    }

    _renderLayers(layers, context){

        // INFO: we render each layer in a promise and chain it to make it sequential - important for the layer index ( this could be simplified with async - await but we target IE11 )
        let renderPromise = Promise.resolve();

        for (let i = 0; i < layers.length; i++) {

            const layer = layers[i];

            renderPromise = renderPromise.then(() => {
                return this._renderLayer(layer, context);
            });
        }

        return renderPromise;
    }

    _renderLayer(layer, context){

        // visible can already be false ( nested parent condition )
        let visible = true;

        // check if layer is conditional
        if(layer.conditions && layer.conditions.length){                                        // NEW: conditionMode config - default AND
            visible = this.conditionsEngine.evaluateConditions(layer.conditions, layer.conditionMode || ConditionsEngine.MODE.AND, 'layer.name=' + layer.name);
        }

        // editor can overwrite visible state
        if(this.settings.editorMode){
            if(this.visibleEditor.hasOwnProperty(layer.name)){
                visible = this.visibleEditor[layer.name];
            }
        }

        /* check if layer is visible changed (never used - because we always call canvas.clear() before rendering this is the only way for a layer to know it visible again)
        if(this.visible.hasOwnProperty(layer.name) && this.visible[layer.name] !== visible){
            layer._visibleChanged = true;
        } else {
            delete layer._visibleChanged;
        } */

        // store visible state  IMPORTANT: after editorMode to sync back
        this.visible[layer.name] = visible;

        // NEW: visible area layers register for refArea consumers - first visible key in document order wins
        // IMPORTANT: area layers must be declared BEFORE their consumer layers (lazy registration during
        // the render pass) - the ordering is guaranteed by the backend config editor
        if(layer.type === 'area' && visible){
            // key defaults to the area layer name
            let key = layer.params.key || layer.name;

            // NEW: print size from a linked dimension option - the entered size and the box fitted to its ratio, read by
            // consumers (_applyAreaParams), the guide box and exports (printArea) through areaParams; null keeps its own
            if(layer.params.optionDimension){
                layer._linkedParams = RenderUtil.linkedAreaParams(layer.params, RenderUtil.getProductOption(layer.params.optionDimension, context.options), context.config);
            } else {
                delete layer._linkedParams;
            }
            // the box with the editor's geometry defaults ( x / y 50, the config size less 100 ), read by the export crop
            layer._box = RenderUtil.areaBox(RenderUtil.areaParams(layer), context.config);

            if(!context.areas[key]){
                context.areas[key] = layer;
            }
        }

        // NEW: preload layer (always render but just set visible flag)
        if(layer.params.preload && layer.type === 'overlay'){ // currently only for overlay
            layer._visible = visible; // set flag to be mapped to fabric options
            visible = true;
        }
        // NEW: export only layer render but keep invisible - then made visible on export
        if(layer.params.exportOnly){
            // only show when layer is selected in editorMode
            layer._visible = RenderUtil.isEditLayer(layer, this.settings.editorMode, this.settings.editLayer);
            layer._visibleCondition = visible; // store visible state for export
            visible = true;
        }

        // only render if visible
        if(visible){
            log.debug('rendering layer: ', layer);

            // nested group layers
            if(layer.layers && layer.layers.length > 0){
                // nested promise chain - ONLY executes all child layers
                return this._renderLayers(layer.layers, context);
            }

            // NEW: materialize the resolved area geometry before the layer renders - _materializeArea skips
            // an active crop write-back override (scoped to the geometry it was derived from, _cropRefArea)
            // IMPORTANT: also for types without a renderer (e.g. overlay_values group parent serving as template)
            if(layer.params.refArea){
                this._materializeArea(layer, context);
            }

            let renderer = RendererRegistry.renderers[layer.type];

            if(renderer){
                // execute renderer
                return renderer.render(layer, context, this._canvas);
            }
        }

        // return resolved promise if layer already visible ( or no renderer )
        return Promise.resolve();
    }

    /*
      NEW: area layer support (docs/layer-area.md)
      materializes the geometry of the area layer registered for params.refArea (context.areas, registered
      by _renderLayer in document order) into the regular refX/refY/refWidth/refHeight/refAngle params -
      all existing ref readers work unchanged. _applyAreaParams is the ONLY writer; an active crop
      write-back override (_cropRefArea, upload-renderer) is never touched. The server skips
      persisting ref* params when params.refArea is set.
      IMPORTANT: refArea REQUIRES params.refRect: true (the master ref box switch, RenderUtil.hasRefRect)
    */
    _materializeArea(layer, context){

        let key = layer.params.refArea;
        let area = context.areas[key] || null;

        // NEW: consumer -> area link for the interaction driven guide toggle (displayType 'i',
        // _syncAreaGuide) - kept fresh even while a crop override is active below
        layer._refAreaLayer = area;

        // crop write-back override (upload-renderer, marked via _cropRefArea): the crop-derived inline
        // geometry only holds for the area geometry it was derived from - when the resolved area changes
        // the override is dropped so the crop re-derives against the new geometry (the layer keeps
        // tracking brand/variant switches)
        if(layer._cropRefArea){
            if(layer._cropRefArea === this._areaGeometryState(area)){
                return;
            }

            delete layer._cropRefArea;

            // reset the crop caches so the crop box re-derives from the new geometry
            delete layer._cropX;
            delete layer._cropY;
            delete layer._cropWidth;
            delete layer._cropHeight;
        }

        // no visible area registered -> default ref box fallback (layer stays visible) - warn once per key
        if(!area){
            log.warn('[live-options] missing:' + key, 'no visible area layer');
        }

        // materialize the resolved geometry into the regular ref* params (single writer)
        this._applyAreaParams(layer.params, area);

        // keep long lived params copies in sync (text editor merged params would otherwise hold stale geometry)
        if(layer._editor){
            this._applyAreaParams(layer._editor, area);
        }

        /*
          NEW: placement projection (docs/layer-area.md section 5)
          a manual placement is never rewritten on geometry changes - interactiveBox layers are projected
          (scaled/clamped, display only) into the resolved area on EVERY render in LayerRenderer._validateArea.
          the geometry change detection below only triggers the stranded check of free layers
        */
        let geometryState = this._areaGeometryState(area);

        if(layer._refAreaGeometry !== geometryState){
            layer._refAreaGeometry = geometryState;

            if(layer._interactive || layer._interactiveScaled){
                layer._areaCheck = true; // free layer stranded check (LayerRenderer._validateArea)
            }
        }
    }

    _areaGeometryState(area){
        if(area){
            let params = RenderUtil.areaParams(area);
            return [params.x, params.y, params.width, params.height, params.angle || 0].join(',');
        }
        // no visible area - truthy sentinel so a crop-during-fallback override holds until a real area appears
        return 'none';
    }

    _applyAreaParams(params, area){
        if(area){
            let areaGeometry = RenderUtil.areaParams(area); // NEW: the box fitted to a linked dimension option
            params.refX = areaGeometry.x;
            params.refY = areaGeometry.y;
            params.refWidth = areaGeometry.width;
            params.refHeight = areaGeometry.height;
            params.refAngle = areaGeometry.angle || 0;
        } else {
            // no visible area - drop the materialized geometry, the ref readers fall back to their
            // own defaults (same 50px inset box as an inline refRect without values)
            delete params.refX;
            delete params.refY;
            delete params.refWidth;
            delete params.refHeight;
            delete params.refAngle;
        }
    }

    /*
      NEW: interaction driven guide visibility (docs/layer-area.md section 9, displayType 'i')
      interactive guides are pre-rendered hidden (area-renderer) and shown here while a consumer is
      dragged/scaled/rotated (object:moving/scaling/rotating call this on every event tick - the
      _activeAreaGuide early-exit makes that a no-op while the guide is already shown), hidden again
      on object:modified (mouse up) - a plain visible flip on the existing objects, NO re-render
      (a render would clear the canvas and kill the active gesture). a gesture can never span a
      re-render, so guides recreated hidden by a render pass are correct by construction
    */
    _syncAreaGuide(layer){

        // resolved area of the interacting consumer (set in _materializeArea)
        let area = layer && layer._refAreaLayer;

        // only interactive display guides follow the selection
        if(area && !(area.params.display && area.params.displayType === 'i')){
            area = null;
        }

        if(this._activeAreaGuide === area){
            return;
        }

        // hide the previous guide (selection moved to another consumer or cleared)
        this._toggleAreaGuide(this._activeAreaGuide, false);
        this._toggleAreaGuide(area, true);

        this._activeAreaGuide = area;

        this._canvas.requestRenderAll();
    }

    _toggleAreaGuide(area, visible){
        if(area && area._displayObjects){
            area._displayObjects.forEach((object) => object.visible = visible);
        }
    }

    _loadUsedFonts() {
        // find used fonts
        let fontsUsed = this._findUsedFonts(this.previewConfig.layers);

        if (fontsUsed.length > 0) {
            return FontLoader.loadFontsByName(fontsUsed);
        } else {
            return Promise.resolve(true);
        }
    }

    _findUsedFonts(layers, fontsUsed = []) {
        for (let i = 0; i < layers.length; i++) {
            const layer = layers[i];

            if (layer.type === 'text') {

                if (layer.params.fontFamily) {
                    fontsUsed.push(layer.params.fontFamily);
                }

                // NEW: text editor find selected font family via option reference
                if (layer.params.option && layer.params.textEditor) {

                    // find product option via layer
                    let productOption = RenderUtil.getProductOption(layer.params.option, this.config.options);

                    if (productOption) {
                        if (productOption._editor && productOption._editor.fontFamily) {
                            fontsUsed.push(productOption._editor.fontFamily);
                        }
                    }
                }

                // NEW: dynamic font via option reference
                if (layer.params.optionFont) {
                    // find product option via layer
                    let productOption = RenderUtil.getProductOption(layer.params.optionFont, this.config.options);

                    if (productOption) {
                        if (productOption.value) {
                            fontsUsed.push(productOption.value);
                        }
                    }
                }

                // NEW: nested text layers now possible with group editor
                if (layer.layers) {
                    this._findUsedFonts(layer.layers, fontsUsed);
                }
            }
        }

        return fontsUsed;
    }

    _isStatic(){

        if(this.settings.editorMode){
            return false;
        }

        for (let i = 0; i < this.previewConfig.layers.length; i++) {
            const layer = this.previewConfig.layers[i];
            // interactive OR crop & upload (only upload supports crop)
            if(layer.params.interactive || (layer.params.crop && layer.type === 'upload')){
                return false;
            }
        }
        return true;
    }

    _watchFormChanges(){
        if(this._productForm){
            // watch for product from option changes
            this._productForm.addEventListener('option:changed', (e) => this._optionChanged(e));
            this._productForm.addEventListener('option:crop', (e) => this._optionCrop(e));
            this._productForm.addEventListener('option:export', (e) => this._optionExport(e));
            this._productForm.addEventListener('option:variant_changed', (e) => this._variantChanged(e));
        } else {
            log.warn('product-preview no productForm found for config: ' + this.config.uuid);
        }
    }

    _subscribeCanvasEvents(){

        let level = log.getLevel();


        this._canvas.on('object:modified', (e) => {
            let object = e.target;

            level <= log.levels.TRACE ? log.debug('object:modified', object) : null;

            // update layer reference
            if(object._layer){
                let layer = object._layer;
                let params = layer.params;

                // coordinates always round to integer
                params.x = Math.round(object.left); // parseFloat(object.left.toFixed(2));
                params.y = Math.round(object.top);

                level <= log.levels.TRACE ? log.debug('=> layer x,y = ', params.x, params.y) : null;

                /* INFO: disabled as in layerRenderer._applyAutoParams
                text scale font size instead of scale
                if(layer.type === 'text'){
                    let newFontSize = object.fontSize * object.scaleX;

                    newFontSize = Math.round(newFontSize);

                    // update rendered object
                    object.fontSize = newFontSize;
                    object.scaleX = 1;
                    object.scaleY = 1;

                    // store updated font size
                    params.fontSize = newFontSize;

                } else { */
                    // IMPORTANT: if this layer is cropped save the exact scale to crop the exact shape (also if not in crop mode)
                    if(params.crop){
                        params.scaleX = object.scaleX;
                        params.scaleY = object.scaleY;
                    } else {
                        // round scale to 3
                        params.scaleX = parseFloat(object.scaleX.toFixed(3));
                        params.scaleY = parseFloat(object.scaleY.toFixed(3));
                    }

                //}

                // rotation
                if(object.angle !== 0){
                    params.angle = Math.round(object.angle);
                }

                // NEW: upload scale box editor write-back (docs/layer-area.md section 10) - resizing the
                // edit object persists the scaled dimensions as the new scale box (x/y are already written
                // above); layer:changed syncs the values to the backend fields like any manual placement.
                // ONLY on an actual scale gesture: a drag must never (re)write width/height - it would
                // silently create the scale box on a mere move
                let action = e.transform && e.transform.action;
                let scaleBoxEdit = action && action.indexOf('scale') === 0
                    && RenderUtil.hasScaleBox(layer)
                    && RenderUtil.isEditLayer(layer, this.settings.editorMode, this.settings.editLayer) && !this.settings.cropMode;
                if(scaleBoxEdit){
                    params.width = Math.round(object.getScaledWidth());
                    params.height = Math.round(object.getScaledHeight());
                }

                // INFO: editor params take priority over layer.params - we merge the interactive properties back into editor
                if(layer._editor){
                    // IMPORTANT: merge to keep editor reference (same reference as on productOption._editor - which then is rendered below)
                    Util.merge(layer._editor, layer.params, ['x','y','scaleX','scaleY','angle']); // ,'fontSize'
                    // render options & editor (could be required if the editor shows x/y or font size)
                    if(this._productForm){
                        this._productForm.dispatchEvent(new CustomEvent('options:render', { detail: null, cancelable: true, bubbles: true }));
                    }
                }

                this._triggerEvent('layer:changed', layer);

                // NEW: interaction ended (mouse up after drag/scale/rotate) - hide the interactive area guide
                this._syncAreaGuide(null);

                // NEW: scale box WYSIWYG snap - re-render so a non-uniform corner drag re-fits the image
                // ratio-kept into the new box (what the merchant sees is what a customer upload will do)
                if(scaleBoxEdit){
                    this.renderPreview();
                }

            } else if(object._layerRef){
                let params = object._layerRef.params;

                // angle snap computed ONCE for both box types - reset if close to zero (currently no reset on UI)
                let angle = 0;
                if(object.angle !== 0){
                    angle = Math.round(object.angle);
                    let roundDegree = 4;
                    if(angle <= roundDegree || angle >= (360-roundDegree)){
                        angle = 0;
                    }
                }

                if(object._layerRef.type === 'area'){
                    // NEW: area layers ARE the geometry - plain x/y/width/height/angle
                    params.x = Math.round(object.left);
                    params.y = Math.round(object.top);
                    // IMPORTANT: width * scaleX instead of getScaledWidth() - the display style strokes the
                    // editor box (_applyDisplayStyle) and getScaledWidth() includes strokeWidth, which would
                    // grow the stored geometry by borderWidth on every edit
                    params.width = Math.round(object.width * object.scaleX); // store dimensions scaled ( indicates scale = 1)
                    params.height = Math.round(object.height * object.scaleY);
                    params.angle = angle;
                } else {
                    params.refX = Math.round(object.left);
                    params.refY = Math.round(object.top);
                    params.refWidth = Math.round(object.getScaledWidth()); // store dimensions scaled ( indicates scale = 1)
                    params.refHeight = Math.round(object.getScaledHeight());
                    params.refAngle = angle; // INFO: delete params.refAngle  did NOT update on server
                }

                log.debug('updated layer rectRef params', params);

                this._triggerEvent('layer:changed', object._layerRef);

                // full render to adjust object based on updated reference
                this.renderPreview();
            }

        });

        this._canvas.on('object:rotating', (e) => {
            let object = e.target;

            if(object._layer){
                let layer = object._layer;
                let params = layer.params;

                if(params.interactive && !RenderUtil.isEditLayer(layer, this.settings.editorMode, this.settings.editLayer)){
                    // Rotation can finish without a scale event, so mark both flags here for exportState().
                    layer._interactive = true;
                    layer._interactiveScaled = true;

                    // NEW: show the interactive area guide while rotating (displayType 'i', hidden again on object:modified)
                    this._syncAreaGuide(layer);
                }
            }
        });

        this._canvas.on('object:moving',(e) => {
            let object = e.target;

            level <= log.levels.TRACE ? log.debug('object:moving', object) : null;

            // coordinates
            if(object._layer){
                let layer = object._layer;
                let params = layer.params;

                // NEW: crop mode interactive box restrictions
                let isCrop = RenderUtil.isCropLayer(layer, this.settings.cropLayer, this.settings.cropLayer);

                // check if layer is interactive  && layer is not edited in backend (avoid saving interactive changes)
                if(isCrop || params.interactive && !RenderUtil.isEditLayer(layer, this.settings.editorMode, this.settings.editLayer)){

                    // flag the layer that it was modified by the user => disables autoPos & sync to backend
                    layer._interactive = true;

                    // NEW: show the interactive area guide while dragging (displayType 'i', hidden again
                    // on object:modified) - idempotent, a no-op while the guide is already shown
                    if(!isCrop){
                        this._syncAreaGuide(layer);
                    }

                    let bounds = { left: 0, top: 0, width: this.previewConfig.width, height: this.previewConfig.height };
                    let ref = { width: this.previewConfig.width, height: this.previewConfig.height };

                    // ref rect box (refArea geometry is already materialized into the ref* params)
                    if(RenderUtil.hasRefRect(layer)) {
                        bounds = RenderUtil.getRefBounds(layer, { config: this.previewConfig });
                        // unrotated ref dimensions (used for interactive box overlay)
                        ref = RenderUtil.getRefDimensions(layer, { config: this.previewConfig });
                    }

                    /*
                      INFO: we call getBoundingRect(true,true) :
                       absolute = true => to get coordinates of the original dimensions
                       calculate = true => to use coordinates of current position instead of .oCoords / .aCoords (because setCoords is not called yet!)
                       docs:  http://fabricjs.com/docs/fabric.Object.html#getBoundingRect
                     */
                    let boundingRect = object.getBoundingRect(true, true);

                    level <= log.levels.TRACE ? log.debug('boundingRect = ', boundingRect) : null;

                    // NEW: center snap (docs/layer-area.md section 9) - the resolved area opts its
                    // consumers into snapping (params.snap on the AREA layer). snapped BEFORE the
                    // containment, which keeps the final say (a centered object always satisfies
                    // the containment, so the clamps below never undo the snap). crop drags
                    // excluded (same rule as the area guide)
                    let area = layer._refAreaLayer;
                    if(!isCrop && area && area.params.snap){
                        let threshold = SNAP_THRESHOLD / this._canvas.getZoom(); // constant screen px feel at any zoom

                        // snap follows the configured center lines (displayCenterH = the VERTICAL
                        // line = x axis, naming matches autoCenterH/V) - an area without center
                        // line params snaps both axes
                        let axes = { x: !!area.params.displayCenterH, y: !!area.params.displayCenterV };
                        if(!axes.x && !axes.y){
                            axes.x = axes.y = true;
                        }

                        RenderUtil.snapToCenter(object, boundingRect, bounds, threshold, axes);
                    }

                    // only allow movement inside the box (shared with the refArea render projection - LayerRenderer._validateArea)
                    if(params.interactiveBox || isCrop){ // NEW: crop mode same as box with overlay
                        // NEW: if the layer is scaled to overlay - allow moving outside of box as long as the box is fully overlayed
                        RenderUtil.containObject(object, layer, boundingRect, bounds, ref, params.autoScaleOverlay || isCrop);
                    }
                    // allow free movement BUT avoid moving object fully outside the box (no way to get it back)
                    else {
                        let buffer = 50 / this._canvas.getZoom(); // buffer of 50px adjusted to the current zoom

                        let min = { x: bounds.left - (boundingRect.width - buffer), y: bounds.top - (boundingRect.height - buffer) };
                        let max = { x: bounds.left + bounds.width - buffer, y: bounds.top + bounds.height - buffer };

                        RenderUtil.clampObject(object, boundingRect, min, max);
                    }

                }

            }

            // Update layer actions tooltip position during drag
            this._updateLayerActionsPosition();

        });


        const onScaling = (e) => {

            let object = e.target;

            level <= log.levels.TRACE ? log.debug('object:scaling', object) : null;

            // coordinates
            if(object._layer) {
                let layer = object._layer;
                let params = layer.params;

                // check if interactive && layer is not edited in backend (avoid saving interactive changes)
                if (params.interactive && !RenderUtil.isEditLayer(layer, this.settings.editorMode, this.settings.editLayer)){

                    // flag the layer that it was modified by the user => disables autoPos & sync to backend
                    layer._interactive = true;
                    layer._interactiveScaled = true; // NEW: disable autoScale

                    // NEW: show the interactive area guide while scaling (displayType 'i', hidden again on object:modified)
                    this._syncAreaGuide(layer);

                    // NEW: autoScaleOverlay could have locked X/Y movement (after scale we allow resize again)
                    if(params.resizable){
                        object.lockMovementX = false;
                        object.lockMovementY = false;
                    }

                    // check if layer is BOX restricted
                    if (params.interactiveBox) {

                        let bounds = { left: 0, top: 0, width: this.previewConfig.width, height: this.previewConfig.height };

                        // refArea geometry is already materialized into the ref* params.
                        if(RenderUtil.hasRefRect(layer)) {
                            bounds = RenderUtil.getRefBounds(layer, { config: this.previewConfig });
                        }

                        if(RenderUtil.isOverlay(layer)){
                            RenderUtil.constrainOverlayScale(object, bounds, e.transform);
                        } else {
                            RenderUtil.constrainScale(object, bounds, e.transform);
                        }
                        this._canvas.requestRenderAll();

                    }
                }


            }

            // Update layer actions tooltip position during scale
            this._updateLayerActionsPosition();

        };

        // Also constrain completed/programmatic scale events before object:modified persists the placement.
        this._canvas.on('object:scaling', onScaling);
        this._canvas.on('object:scaled', onScaling);

        this._canvas.on('selection:created', (e) => {

            // ignore crop mode selections
            if (this.settings.cropMode) {
                return;
            }

            // first selection
            let object = e.selected ? e.selected[0] : null;

            level <= log.levels.TRACE ? log.debug('selection:created', object) : null;

            let layer =  object?._layer; // EXCLUDE: refRect || object?._layerRef;
            if(layer){
                // only trigger if user interaction
                e.e && this._triggerEvent('layer:selected', layer);
                this._showLayerActionsTooltip(layer);
            }

        });

        this._canvas.on('selection:updated', (e) => {

            // ignore crop mode selections
            if (this.settings.cropMode) {
                return;
            }

            // first selection
            let object = e.selected ? e.selected[0] : null;

            level <= log.levels.TRACE ? log.debug('selection:updated', object) : null;

            let layer = object?._layer; // EXCLUDE: refRect || object?._layerRef;
            if(layer){
                // only trigger if user interaction
                e.e && this._triggerEvent('layer:selected', layer);
                this._showLayerActionsTooltip(layer);
            }
        });

        this._canvas.on('selection:cleared', (e) => {

            if (!e.e) {  // ignore if not caused by a user interaction (e.g. when calling canvas.discardActiveObject()) (which is called every render event)
                return;
            }

            // first de-selection
            let object = e.deselected ? e.deselected[0] : null;

            level <= log.levels.TRACE ? log.debug('selection:cleared', object) : null;

            let layer = object?._layer; // EXCLUDE: refRect || object?._layerRef;
            if(layer){
                this._triggerEvent('layer:deselected', layer);
            }

            // Hide layer actions tooltip on deselection
            this._hideLayerActionsTooltip();

            // NEW: safety hide for the interactive area guide (drags normally end via object:modified;
            // this covers gestures interrupted by a click-away/ESC deselection)
            this._syncAreaGuide(null);
        });

        this._canvas.on('selection:cleared', (e) => {
            level <= log.levels.TRACE ? log.debug('selection:cleared') : null;
        });

        // Listen for centering action events
        this.element.addEventListener('layer:action:centerH', (e) => {
            this._centerLayer(e.detail, 'horizontal');

            this._updateLayerActionsPosition();
        });

        this.element.addEventListener('layer:action:centerV', (e) => {
            this._centerLayer(e.detail, 'vertical');

            this._updateLayerActionsPosition();
        });

        /* DEBUG: bounding box visualizer
        this._canvas.on('after:render', () => {
            this._canvas.contextContainer.strokeStyle = '#555';

            this._canvas.forEachObject((obj) => {
                var bound = obj.getBoundingRect();

                this._canvas.contextContainer.strokeRect(
                    bound.left + 0.5,
                    bound.top + 0.5,
                    bound.width,
                    bound.height
                );

            })
        }); */

    }

    _variantChanged(event){
        // store variant id ( provided on render context )
        this.settings.variantId = event.detail.variantId;

        this._clearVisibleEditor();

        // render preview again
        this.renderPreview().then((context) => {

            if(!this.static){
                // NEW: auto crop layer set on context (allows change from other options)
                if(!this.settings.cropMode && context.autoCrop){
                    this._optionCrop(event, context.autoCrop); // provide layer reference directly
                }
            }

        });
    }

    _optionChanged(event){
        let option = event.detail;

        this._clearVisibleEditor();

        // INFO: we could find all the referenced options in the conditions and only renderPreview if an option changed that is used in a condition

        // render preview again
        this.renderPreview().then((context) => {

            if(!this.static){

                // NEW: auto crop layer set on context (allows change from other options)
                if(!this.settings.cropMode && context.autoCrop){
                    this._optionCrop(event, context.autoCrop); // provide layer reference directly
                }
                // NEW: auto select layer if interactive
                else if(option._layer && option._layer.params.interactive){
                    // IMPORTANT: select layer after renderPreview (layer could have just been added with this option change)
                    this.selectLayer(option._layer.name, false);
                }
            }

        });
    }

    _optionCrop(event, layer){
        let option = event.detail;

        // ignore if the view is not active (multi view)
        if(!this.active){
            return;
        }
        // try to find layer if not provided
        else if(!layer) {

            // IMPORTANT: multiple layers can point to the same upload option & we have to loop over the actual rendered objects
            let objects = this._canvas.getObjects();

            for (let i = 0; i < objects.length; i++) {
                const object = objects[i];
                // find the matching object layer
                if (object._layer && (object._layer.params.option === option.name) && object._layer.params.crop) {
                    layer = object._layer;
                    break;
                }
            }
        }

        if(this.settings.cropMode){
            this.settings.cropMode = false;

            this.settings.cropLayer = '';

            // IMPORTANT: re-calc the zoom based on the width only (required to support preview resize during crop)
            let zoom = this._getContainerWidth() / this.previewConfig.width;

            // NEW: max height defined just reset to the max height (required for the popup use case)
            if(this.settings.maxHeight){
                zoom = this.settings.maxHeight / this.previewConfig.height;
            }

            this._canvas.setViewportTransform([zoom, 0, 0, zoom, 0, 0]);

            // remove highlight
            this._highlight(false);

            this._wrapper.removeChild(this._cropEl);
            delete this._cropEl;

        } else {

            // skip if not found
            if(!layer){
                return;
            }

            this.settings.cropMode = true;

            this.settings.cropLayer = layer.name;

            let params = layer.params;

            // crop dimensions
            let cropDimensions = { width: this.previewConfig.width, height: this.previewConfig.height };

            // crop cancel restore options (we use object properties - could have been autoScaled before and not saved on params (only interaction updates params)
            let restore = { x: layer._object.left, y: layer._object.top, scaleX: layer._object.scaleX, scaleY: layer._object.scaleY, rotate: layer.params.rotate | 0 };

            // reset interactive to rescale to overlay & IMPORTANT: restore to the exact cooridnates saved above!
            let restoreInteractive = { _interactive: layer._interactive, _interactiveScaled: layer._interactiveScaled }

            // keep an applied placement that still covers the crop box, otherwise re-fit once (dimension ratio change, shrunk photo)
            if(!this._coversCropBox(layer)){
                delete layer._interactive;
                delete layer._interactiveScaled;
            }

            // if ref box is defined - zoom in for better cropping support
            if(RenderUtil.hasRefRect(layer)){

                let context = this._renderContext();

                // ref dimensions to support full area or box
                let refDimensions = RenderUtil.getRefDimensions(layer, context);
                let refCoordinates = RenderUtil.getRefCoordinates(layer, context);

                // 50px buffer on each side
                let buffer = 100;

                // NEW: reduce buffer on mobile devices (otherwise zoom can be less than current zoom)
                if(window.innerWidth < 500){
                    buffer = 50;
                    // max height set > reduce buffer more (popup)
                    if(this.settings.maxHeight && this.settings.maxHeight < 200){
                        buffer = 20;
                    }
                }

                // zoom to fit the bigger side
                let zoomLevel = Math.min((this._canvas.getWidth() - buffer) / refDimensions.width, (this._canvas.getHeight() - buffer) / refDimensions.height);

                // NEW: only zoom if the calculated zoom is actually bigger (popup mobile can be quite small)
                let currentZoom = this._canvas.getZoom();
                if(zoomLevel > currentZoom){
                    // max zoom 2x (mainly to avoid preview to blurry)
                    zoomLevel = Math.min(zoomLevel, 2);

                    let centerX = refCoordinates.x + (refDimensions.width / 2);
                    let centerY  = refCoordinates.y + (refDimensions.height / 2);

                    // calc center zoom x/y
                    let zoomX = (-centerX * zoomLevel) + this._canvas.getWidth()  / 2;
                    let zoomY  = (-centerY * zoomLevel) + this._canvas.getHeight() / 2;

                    // update the canvas viewport
                    this._canvas.setViewportTransform([zoomLevel, 0, 0, zoomLevel, zoomX, zoomY]);
                }

                // update crop dimensions
                cropDimensions.width = params.refWidth;
                cropDimensions.height = params.refHeight;
            }

            this._cropEl = document.createElement('div');

            // show crop animation (only shown once)
            if(window.clCropHelp !== false){

                this._cropEl.classList.add('cl-po--crop-help');

                // show touch or cursor animation
                if('ontouchstart' in window || navigator.msMaxTouchPoints){
                    this._cropEl.classList.add('touch');
                } else {
                    this._cropEl.classList.add('cursor');
                }

                const hideCropHelp = () => {
                    if(this._cropEl){
                        this._cropEl.classList.remove('cl-po--crop-help');
                        // remove itself
                        this._canvas.off('object:moving', hideCropHelp);
                        // flag to not show animation again
                        window.clCropHelp = false;
                    }
                };

                // hide crop animation when object is moved
                this._canvas.on('object:moving', hideCropHelp);

                // auto hide after 9 seconds (3 full animations)
                setTimeout(hideCropHelp, 9_000);
            }

            const onCrop = () => {

                // Crop apply can persist a pure crop change without a drag, so mark the same flags exportState() reads.
                layer._interactive = true;
                layer._interactiveScaled = true;

                // trigger modified IMPORTANT: updates layer params - required if images was not moved in crop
                this._canvas.fire('object:modified', { target: layer._object });

                this._optionCrop({ detail: option }, layer);
            }

            // NEW: customer can click on add to cart (especially if the crop overlay is not shown)
            // in this case we manually have to apply the crop if we are still in crop mode (event is triggered by upload option)
            this._productForm.addEventListener('option:crop:apply', (e) => {
                if(this.settings.cropMode){
                    onCrop();
                }
            }, { once: true });

            // render into cropEl
            render(productPreviewCropTemplate((up) => {

                let object = layer._object;

                // disable autoScale
                layer._interactiveScaled = true;

                // scale in 10% increments of the smaller crop dimensions
                let step = Math.min(cropDimensions.width, cropDimensions.height) / 10;
                let scaleStep = step / object.width;

                // keep the center fixed (step/2 offsets drift for non-square images)
                let center = object.getCenterPoint();

                if(up){
                    object.scaleX += scaleStep;
                    object.scaleY += scaleStep;
                } else {
                    object.scaleX -= scaleStep;
                    object.scaleY -= scaleStep;

                    // NEW: only avoid scaling less then crop dimensions if the object is required to be fully overlayed
                    if(layer.params.autoScaleOverlay){
                        // avoid scaling less then crop dimensions
                        if(Math.round(object.getScaledWidth()) < cropDimensions.width || Math.round(object.getScaledHeight()) < cropDimensions.height){
                            object.scaleX += scaleStep;
                            object.scaleY += scaleStep;
                            return;
                        }
                    }
                }

                object.setPositionByOrigin(center, 'center', 'center');

                // trigger object:moving handler - INFO: this auto centers the object to make sure it overlays the crop are using the params.autoScaleOverlay logic!
                this._canvas.fire('object:moving', { target: layer._object });

                this._canvas.renderAll();

            }, (degrees) => {

                // layer._rotate can contain default from autoRotate
                layer.params.rotate = layer._rotate || layer.params.rotate || 0;

                layer.params.rotate += degrees;
                layer.params.rotate = layer.params.rotate % 360;

                // Crop rotation bypasses Fabric's rotate gesture, so mark both flags explicitly.
                layer._interactive = true;
                layer._interactiveScaled = true;

                // full render required for upload-layer to rotate image
                this.renderPreview();

            }, () => {

                // restore params before crop ( option crop call below triggers full render again )
                Util.merge(layer.params, restore);
                Util.merge(layer, restoreInteractive);

                // stop crop
                this._optionCrop({ detail: option });
            }, onCrop ), this._cropEl);

            this._wrapper.appendChild(this._cropEl);

            // highlight the crop
            this._highlight(true);

            // NEW: simpler top cut off check
            let rect = this._wrapper.getBoundingClientRect();
            if(rect.top < 0 && window.scrollTo){
                // INFO: window.scrollTo better support to scroll above target
                // scroll 50px above element
                window.scrollTo(rect.left, rect.top + window.scrollY - 50);
            }

            /* DEPRECATED: scroll preview into view   // remove check: && !Util.isScrolledIntoView(this._wrapper)  (did not always work)
            if(this._wrapper.scrollIntoView){
                this._wrapper.style.scrollMargin = '50px'; // this makes the scrollIntoView a bit above the element
                this._wrapper.scrollIntoView({behavior: "smooth", block: "start", inline: "nearest"});
                this._wrapper.style.scrollMargin = '';
            } */
        }

        // de-select (avoids re-selection on crop Mode end)
        this._canvas.discardActiveObject();

        // render with new cropMode
        this.renderPreview().then(() => {
            if(this.settings.cropMode){
                this.selectLayer(this.settings.cropLayer);
            }
        });

    }

    _coversCropBox(layer){
        let box = { left: 0, top: 0, width: this.previewConfig.width, height: this.previewConfig.height };
        if(RenderUtil.hasRefRect(layer)){
            box = RenderUtil.getRefBounds(layer, { config: this.previewConfig });
        }
        // 1px tolerance for float rounding of the applied scale
        let b = layer._object.getBoundingRect(true, true);
        return b.left <= box.left + 1 && b.top <= box.top + 1
            && b.left + b.width >= box.left + box.width - 1 && b.top + b.height >= box.top + box.height - 1;
    }

    _optionExport(event) {
        let option = event.detail;

        let previewConfig = this.previewConfig;

        // optional view name filter
        if(option.params.exportView && option.params.exportView !== previewConfig.name){
            return;
        }

        // TODO config which export? default to original
        // TODO support custom exports definitions via server - requires server download

        // NEW: crop the original export
        let cropExport = previewConfig.exportConfig.crop && previewConfig.exportConfig.cropConfig.width
            ? previewConfig.exportConfig.cropConfig : false;

        // NEW: format settings (default png to avoid breaking existing config) - empty could mean auto detect based if masks are used
        let format = previewConfig.exportConfig.originalFormat || 'png';

        // full size png export
        let data = this.export({
            width: previewConfig.width,
            height: previewConfig.height
        }, cropExport, true, 'excludeOriginal', format, 0.8, false);

        // download in browser
        if(data){
            this.downloadBlob(data, (option.params.exportName || previewConfig.name) + '.' + format);
        }
    }

    _clearVisibleEditor(){
        // reset the overwritten visible states to use the evaluated states from the conditions
        if(this.settings.editorMode){
            this.visibleEditor = {};
        }
    }

    _optionLayerUpdate(option){
        /* EXAMPLE: only rendering a single layer:
            - on first render the layer reference is set on the option
            - we have to remove the object because the renderer uses canvas.add()
            - we have to restore the z-index
            - needs to be called in _optionChanged / _variantChanged

           NOT USED BECAUSE:
            - full render is simpler
            - full render uses cached objects
            - renderPreview would still need to be executed in the case of conditions based on this Option

         */
        if(option._layer && option._layer._object){

            let index = this._canvas.getObjects().indexOf(option._layer._object);

            this._canvas.remove(option._layer._object);

            return this._renderLayer(option._layer).then((object) => {
                this._canvas.moveTo(object, index);
            });
        }
    }

    _watchWindowResize() {

        // only resize after 200ms
        let resizeOnce = Util.debounced(200, (e) => {
            this._resizeCanvas();

            if(this.settings.stickyMode){
                this._stickyPreview(true);
            }
        });

        // first trigger manually
        this._resizeCanvas();

        // watch window resize
        window.addEventListener('resize', resizeOnce);
    }

    _watchWindowScroll(){

        let ticking = false;

        // watch window scroll
        window.addEventListener('scroll', () => {

            if (!ticking) {

                // NEW: we always watch scroll event - only execute requestAnimation frame if needed
                if(this.settings.sticky || this._highlightEl) {

                    // INFO: request animation frame instead of Util.debounced for smoother transition
                    window.requestAnimationFrame(() => {

                        if (this.settings.sticky) {
                            this._stickyPreview();
                        }

                        // if currently highlighted > re-build highlight to fit new position (required when theme has sticky elements)
                        if (this._highlightEl) {
                            this._highlight(true);
                        }

                        ticking = false;
                    });

                    ticking = true;
                }
            }

        });

        // only if enabled
        if(this.settings.sticky) {
            // gallery parent active change sticky update support (no direct reference, we listen on bubbled up event on window element)
            window.addEventListener('preview:active', () => this._stickyPreview());
        }
    }

    _watchPreviewActive() {
        window.addEventListener('preview:active', () => {
            if (this.active && !this.static) {
                // This preview just became active - re-show tooltip if an object is still selected
                const activeObject = this._canvas?.getActiveObject();
                const layer = activeObject?._layer;
                if (layer) {
                    this._showLayerActionsTooltip(layer);
                }
            } else {
                this._hideLayerActionsTooltip();
            }
        });
    }

    _getContainerWidth(){
        // INFO: because we hide this._preview we have to scale based on wrapper
        let width = this._wrapper.offsetWidth;

        // NEW: if the full preview wrapper is hidden (variant gallery with other variant selected) check all parent nodes until we find a width to scale into
        let node = this._wrapper;
        while(width === 0 && node.parentNode) { // edge case no parent node
            // check parent until width is not 0
            node = node.parentNode;
            if(node.offsetWidth > 0){
                // HACKY: this size can include padding/margin we remove them to avoid scaling to big
                width = Util.findInnerDimensions(node).width;
            }
        }

        return width;
    }

    _responsiveCanvasHeight(){

        // skip height resize when we are cropping
        if(!this.settings.cropMode){

            // INFO: because the dimensions are fixed - to support "responsive" dimensions based on an object we fit the height to include layers marked with responsive ( width is already responsive via _resizeCanvas )
            let height = 0;

            // loop trough all objects on the canvas to find the max height
            this._canvas.forEachObject((obj) => {
                // only if visible & marked as responsive
                if(obj.visible && obj._layer && obj._layer.params.responsive){
                    let bound = obj.getBoundingRect();
                    let y = bound.top + bound.height;

                    if(y > height){
                        height = y;
                    }
                }

            });

            let scaledHeight = this.previewConfig.height * this._canvas.getZoom();

            // only reduce height
            if(height > 0 && height < scaledHeight){
                this._canvas.setHeight(height);
            } else {
                this._canvas.setHeight(scaledHeight); // reset
            }
        }

    }

    _resizeCanvas(){

        // fit into preview width and keep ratio , source: https://stackoverflow.com/a/60614910
        const ratio = this._canvas.getWidth() / this._canvas.getHeight();
        const containerWidth = this._getContainerWidth();

        let scale = containerWidth / this._canvas.getWidth();
        let zoom  = this._canvas.getZoom() * scale;

        // update if not already scaled
        if(zoom !== 1){
            let dimensions = { width: containerWidth, height: containerWidth / ratio };

            if(this.settings.maxHeight && dimensions.height > this.settings.maxHeight){
                dimensions.height = this.settings.maxHeight;
                dimensions.width = dimensions.height * ratio;
                // IMPORTANT: re calc scale and zoom
                scale = dimensions.width / this._canvas.getWidth();
                zoom = this._canvas.getZoom() * scale;
            }

            this._canvas.setDimensions(dimensions);
            this._canvas.setZoom(zoom);
            //this._canvas.setViewportTransform([zoom, 0, 0, zoom, 0, 0]);  // same as setZoom
        }

        // if currently highlighted > re-build highlight to fit new preview size
        if(this._highlightEl){
            this._highlight(true);
        }
    }

    _highlight(show){

        // NEW: flag to disable overlay    || popup disable highlighting (messed up with the positioning - display:fixed solved it on desktop but not mobile)
        if(!ThemeConfig.productCropOverlay || Util.findParent(this.element, '.cl-po--popup-preview')){
            show = false;
        }

        // clear old highlight
        if(!show && this._highlightEl){
            document.body.removeChild(this._highlightEl);
            delete this._highlightEl;
            document.body.classList.remove('cl-highlight');
        }

        if(show){
            let add = false;
            // NEW: we only create the highlight element once and then update the style
            if(!this._highlightEl){
                this._highlightEl = document.createElement('div');
                this._highlightEl.className = 'cl-po--preview-highlight';
                add = true;
            }

            let position = Util.getCalculatedPosition(this._wrapper);

            const requiredPadding = 10 * 2;

            // source: https://github.com/kamranahmedse/driver.js/blob/master/src/core/stage.js#L80
            const width = (position.right - position.left) + (requiredPadding);
            const height = (position.bottom - position.top) + (requiredPadding);

            // Show the stage
            this._highlightEl.style.display = 'block';
            this._highlightEl.style.position = 'absolute';
            this._highlightEl.style.width = `${width}px`;
            this._highlightEl.style.height = `${height}px`;
            this._highlightEl.style.top = `${position.top - (requiredPadding / 2)}px`;
            this._highlightEl.style.left = `${position.left - (requiredPadding / 2)}px`;

            // add to body if new
            if(add){
                document.body.appendChild(this._highlightEl);
                document.body.classList.add('cl-highlight'); // class on body for custom styling (e.g gallery arrows hidden)
            }
        }
    }

    _stickyPreview(resize = false){

        // use actual preview element to get the exact position and size
        let canvasEl = this._canvas.lowerCanvasEl;

        let rect = canvasEl.getBoundingClientRect();

        // default 0 = full visible  (sticky mobile overwrites this)
        let maxTop = this._stickyMaxTop || 0;

        // VISIBLE in view   OR view not active (gallery - allows switching sticky preview)   OR disable sticky preview in cropMode OR disable sticky in zoomMode  OR NEW: exclude hidden preview (no dimensions - theme layout or manual display:none;)
        if(rect.top > maxTop || !this.active || this.settings.cropMode || this.settings.zoomMode || (rect.width === 0 || rect.height === 0)){

            // remove sticky mode when preview fully visible
            if(this.settings.stickyMode){

                log.debug('----> sticky end', canvasEl);

                // remove event listeners
                this.element.removeEventListener('layer:changed', this._syncSticky);
                this.element.removeEventListener('preview:rendered', this._syncSticky);

                // remove from dom
                document.body.removeChild(this._stickyEl);

                // reset references
                delete this._stickyEl;
                delete this._stickyImgEl;
                delete this._stickyTarget;
                delete this._stickyMaxTop;

                this.settings.stickyMode = false;
            }

        }
        // NOT visible
        else {

            let isMobile = false;
            let mobileHeight;
            let mobileHeightCheck;  // NEW: additional setting for mobile overlay (but keep custom window setting as first to avoid affecting existing setups)
            let mobileSize = window.clStickyMobileSize || this.settings.stickyMobileSize || 25; // default 25%

            // stay sticky next to the options element
            this._stickyTarget = this._stickyTarget || document.querySelector('#' + this.config.uuid);

            let targetRect = this._stickyTarget.getBoundingClientRect();

            // target is below the preview (mobile stacked view) && NEW: additional mobile check for screen width
            if(targetRect.top > rect.bottom && window.innerWidth <= 768){
                isMobile = true;
                // height based on screen height
                mobileHeight = (window.innerHeight / 100) * mobileSize;

                // height check is used to trigger show/hide of mobile sticky view
                if(this.settings.stickyMobile === 'overlay'){
                    mobileHeightCheck = mobileHeight; // overlay trigger when parts of the preview is not visible to fully overlay with the sticky variant
                } else {
                    mobileHeightCheck = 0; // rest only show if completely hidden
                }
            }

            // sticky preview already shown > update position if needed
            if(this.settings.stickyMode){

                let stickyRect = this._stickyEl.getBoundingClientRect();

                // target still visible?
                if(targetRect.bottom > 0){

                    let diff = stickyRect.height - targetRect.bottom;

                    // check if target (options) are scrolled below the shown preview
                    if(diff > 0){
                        // move upwards to hide the preview (sticky effect)
                        this._stickyEl.style.top =  (diff * -1) + 'px';
                    } else {
                        this._stickyEl.style.top = '0px';
                    }
                } else {
                    // not visible hide via full negative height (+5 just to fully hide shadows)
                    this._stickyEl.style.top = ((stickyRect.height + 5) * -1) + 'px';
                }

                // window was resized and sticky preview is still required > update x position and dimensions
                if(resize){

                    if(isMobile){

                        // reset previous (desktop>mobile switch)
                        this._stickyEl.classList.add('mobile');
                        this._stickyEl.classList.add(this.settings.stickyMobile);
                        this._stickyEl.style.removeProperty('left');
                        this._stickyEl.style.removeProperty('width');

                        // update height & max top
                        this._stickyImgEl.style.maxHeight = mobileHeight + 'px';
                        this._stickyMaxTop = (rect.height - mobileHeightCheck) * -1;

                    } else {

                        // reset previous (mobile>desktop)
                        this._stickyEl.classList.remove('mobile');
                        this._stickyImgEl.style.removeProperty('height');

                        this._stickyEl.style.left = rect.left + 'px';
                        this._stickyEl.style.width =  rect.width + 'px'; // width equal to preview
                    }
                }

            } else {

                log.trace('---> sticky check' + targetRect.bottom + ' > ' + rect.bottom);

                // ONLY start sticky mode if the options are actually longer then the preview element
                if(targetRect.bottom > rect.bottom){

                    // target is below the preview (mobile stacked view)
                    if(isMobile){

                        // only show when the still visible part of the preview is smaller than the sticky mobile preview height
                        if(rect.bottom > mobileHeightCheck){
                            return;
                        }

                        // dynamic maxTop to toggle on/off (mobile sticky is smaller in size we have to remove the sticky overlay earlier)
                        this._stickyMaxTop = (rect.height - mobileHeightCheck) * -1;
                    }

                    this._stickyEl = document.createElement('div');
                    this._stickyEl.className = 'cl-po--preview-sticky';
                    this._stickyEl.style.position = 'fixed';

                    this._stickyImgEl = document.createElement('img');

                    if(isMobile){
                        this._stickyEl.classList.add('mobile');
                        this._stickyEl.classList.add(this.settings.stickyMobile);

                        this._stickyImgEl.style.maxHeight = mobileHeight + 'px';
                    } else {
                        this._stickyEl.style.left = rect.left + 'px';
                        this._stickyEl.style.width =  rect.width + 'px'; // width equal to preview
                    }

                    this._stickyEl.style.top = '0px';

                    this._stickyEl.addEventListener('click', (e) => {

                        let calculatedPosition = Util.getCalculatedPosition(canvasEl);

                        // scroll 50 px above real canvas > triggers scroll event & removes sticky view
                        window.scroll({top: calculatedPosition.top - 50, behavior: 'smooth'});
                    });

                    this._stickyEl.appendChild(this._stickyImgEl);

                    this._syncSticky();

                    // add to body for sticky position to work
                    document.body.appendChild(this._stickyEl);

                    // layer change (interactive updates)
                    this.element.addEventListener('layer:changed', this._syncSticky);
                    // rendered (options change)
                    this.element.addEventListener('preview:rendered', this._syncSticky);

                    this.settings.stickyMode = true;

                    log.debug('--> sticky start', this._stickyEl, this._stickyTarget);
                }

            }
        }

    }

    // arrow function to allow keep event handler this pointer
    _syncSticky = () =>  {
        let options = {
            format: 'png', // for transparent support
            multiplier: this.previewConfig.width / this._canvas.getWidth() // full size render same as currently shown
        }

        // update sticky img src with rendered canvas
        this._stickyImgEl.src = this._canvas.toDataURL(options);
        // required to render full canvas again (without this canvas is only white)
        this._canvas.requestRenderAll();
    };


    // select gallery thumbnail to show live preview
    _syncThemeGallery(){
                                   // NEW: exclude logic in editor mode (can cause clicks on wrong items and endless loops)
        if(ThemeConfig.themeGallery && !this.settings.editorMode){

            // NEW: dawn open theme gallery specific auto select code
            if(this.shopifyStore.isTheme('Dawn')) {
                let mediaGalleryEl = Util.findParent(this.element, 'media-gallery');
                if (mediaGalleryEl && mediaGalleryEl.setActiveMedia) {
                    let mediaItemEl = Util.findParent(this.element, '.product__media-item');
                    if (mediaItemEl && mediaItemEl.dataset.mediaId) { // is null when app gallery is active
                        // NEW: overwrite scroll into view on the main live preview element (the SliderComponent calls this function)
                        this.element.scrollIntoView = function(){}; //  INFO: variant changes still triggers on desktop as scrollIntoView is called on the other variant media images
                        mediaGalleryEl.setActiveMedia(mediaItemEl.dataset.mediaId, true);
                        let sliderEl = Util.findParent(this.element, '.slider');
                        if(sliderEl && sliderEl.scrollTo){ // IMPORTANT: mobile slider - reset to first slide
                            sliderEl.scrollLeft = 0; // setActiveMedia should move the current media (preview) slide to the start
                        }

                        // NEW: dawn > 13.0.0 use other way to scroll - we patch the scrollTo function to avoid scrolling
                        // https://github.com/Shopify/dawn/blob/main/assets/media-gallery.js#L56
                        window._scrollTo = window._scrollTo || window.scrollTo; // store original (once)
                        window.scrollTo = function(){
                            // ignore scroll and restore original
                            window.scrollTo = window._scrollTo;
                        };

                        return; // skip below theme gallery logic
                    }
                }
            } else if(this.shopifyStore.isTheme('Horizon')){

                // NEW: Horizon theme gallery specific auto select code
                let slideShowEl = Util.findParent(this.element, 'slideshow-component');

                // we always select the first slide (options-app init is fixed on first slide so this is OK)
                if(slideShowEl && typeof slideShowEl.select === 'function' && slideShowEl.current !== 0) {
                    slideShowEl.select(0, new CustomEvent('lpo'))
                }

                return; // skip below theme gallery logic
            }

            try {
                // not disabled (false)
                if(this._themeGalleryImgEl !== false){

                    // INFO: exclude variant changes if the live preview product layer does not show the current variant (allows to change the variant without the gallery thumbnail changing to the live preview)
                    // not supported when live variantOptions are active (also triggers option:changed event which then skips this check)
                    if(this._themeGalleryVariant && this.settings.variantId !== this._themeGalleryVariant){
                        this._themeGalleryVariant = this.settings.variantId;
                        return;
                    }

                    // not yet resolved (undefined)
                    if(!this._themeGalleryImgEl){

                        // only execute sync on the main preview           && exclude when gallery is active ||  when gallery is used without thumbnails
                        if(typeof this.previewConfig.views !== 'undefined' && (!this.previewConfig.gallery || this.config.previewConfig.galleryConfig.hideThumbnails === true)){

                            let productImage;

                            if(this.settings.product.media && this.settings.product.media.length){

                                var imgIndex = 0;

                                // NEW: same logic as from options-app to find preview location - find the referenced image to find the thumbnail
                                for (let i = 0; i < this.previewConfig.layers.length; i++) {
                                    const layer = this.previewConfig.layers[i];
                                    if(layer.type === 'product'){

                                        // set variant id to detect if only the variant id changed to skip the thumbnail select
                                        this._themeGalleryVariant = this.settings.variantId;

                                        if(layer.params.imgPos){
                                            if(this.settings.product.media.length > (layer.params.imgPos - 1)){
                                                imgIndex = layer.params.imgPos - 1;
                                            }
                                        }
                                        // if the variant is not shown - remove variant id to always select the gallery thumbnail on every change
                                        else if(layer.params.variant){
                                            delete this._themeGalleryVariant;
                                        }

                                        break;
                                    }
                                }

                                productImage = this.settings.product.media[imgIndex];
                            }

                            if(productImage){

                                let galleryImgElList;

                                // basename to find matching image element
                                let productImageBaseName = Util.pathBaseName(productImage.src, false);
                                // use selector if provided
                                if(this.settings.selectorThumbnail){
                                    galleryImgElList = document.querySelectorAll(this.settings.selectorThumbnail);

                                    log.debug('found ' + galleryImgElList.length + ' gallery thumbnails with selector: ', this.settings.selectorThumbnail, galleryImgElList);
                                }
                                // default no selector OR no match for selectorThumbnail
                                if(!galleryImgElList || galleryImgElList.length === 0){

                                    galleryImgElList = []; // convert NodeList to Array

                                    // search img tags that contain the product img basename
                                    let productImgElList = document.querySelectorAll('img[src*="'+productImageBaseName+'"], img[data-src*="'+productImageBaseName+'"], img[data-srcset*="'+productImageBaseName+'"]');

                                    if(productImgElList.length > 0){
                                        // hardcoded exclude of cart images (often contain the same product image when in cart)
                                        for (let i = 0; i < productImgElList.length; i++) {  // array loop instead of .filter because NodeList
                                            const imgEl = productImgElList[i];
                                            // child of cart form signals that this is a cart image
                                            let parentForm = Util.findParent(imgEl, 'form');
                                            if(parentForm === null || parentForm.action.indexOf('cart') === -1){
                                                galleryImgElList.push(imgEl);
                                            }
                                        }
                                    }

                                    log.debug('found ' + galleryImgElList.length + ' gallery thumbnails with src: ', productImageBaseName, galleryImgElList);
                                }

                                if(galleryImgElList.length){

                                    // INFO: custom selector and default img.src search return multiple matches - find best filename match
                                    let nameDiff = Number.MAX_VALUE;

                                    for (let j = 0; j < galleryImgElList.length; j++) {

                                        const galleryImgEl = galleryImgElList[j];

                                        // find best src IMPORTANT: avoid src with data urls
                                        let src = (galleryImgEl.src && galleryImgEl.src.indexOf('data:') === -1 ? galleryImgEl.src : null) || galleryImgEl.dataset['src'] || galleryImgEl.dataset['srcset'];
                                        // extract basename without extension
                                        let basename = Util.pathBaseName(src, false);
                                        // important to remove dimensions to check the actual filename parts
                                        basename = this.shopifyStore.stripImageDimension(basename);
                                        let left = basename.replace(productImageBaseName, '');
                                        let diff = left.length;
                                        // select if less diff then the previous
                                        if(diff < nameDiff){
                                            nameDiff = diff;
                                            this._themeGalleryImgEl = galleryImgEl;
                                        }
                                    }

                                    log.debug('selected theme gallery thumbnail: ', this._themeGalleryImgEl);
                                }
                            }

                        } else {
                            // avoid further checks
                            this._themeGalleryImgEl = false;
                        }
                    }

                    // resolved > click to select right gallery view which shows the live preview
                    if(this._themeGalleryImgEl && this._themeGalleryImgEl.click){

                        // check if the preview is currently hidden
                        let hidden = Util.isHidden(this._wrapper);

                        // required for some theme galleries (300ms to wait for their slide animation to finish)
                        if(ThemeConfig.themeGalleryTimeout){
                            setTimeout(() => this._themeGalleryImgEl.click(), 300);
                        } else {
                            this._themeGalleryImgEl.click();
                        }

                        // hidden preview - the dimensions can be wrong > resize to make sure the preview is fitted inside the gallery
                        if(hidden){
                            // give some time for theme gallery to show the preview  NEW: window resize also updates gallery of themes and canvas
                            setTimeout(() => window.dispatchEvent(new Event('resize')), 500);
                        }
                    }
                }
            } catch (error){
                log.error('error syncing theme gallery ', error);
            }
        }
    }

    _triggerEvent(name, data){
        // custom event allow cancel via e.preventDefault & bubble to trigger up the DOM tree
        const event = new CustomEvent(name, { detail: data, cancelable: true, bubbles: true });
        // we currently use the root element to bubble up to the productForm
        this.element.dispatchEvent(event);
    }

    _patchCanvasTouchScroll() {
        // only patch once
        if(!window.clCanvasTouchStart){
            // source: https://github.com/fabricjs/fabric.js/issues/5903#issuecomment-811015515
            // save original ref
            window.clCanvasTouchStart = fabric.Canvas.prototype._onTouchStart;
            fabric.util.object.extend(fabric.Canvas.prototype, {
                _onTouchStart: function(e) {
                    var target = this.findTarget(e);
                    // if allowTouchScrolling is enabled, no object was at the
                    // the touch position and we're not in drawing mode, then
                    // let the event skip the fabricjs canvas and do default
                    // behavior
                    if (this.allowTouchScrolling && !target && !this.isDrawingMode) {
                        // returning here should allow the event to propagate and be handled
                        // normally by the browser
                        return;
                    }

                    // otherwise call the default behavior
                    window.clCanvasTouchStart.call(this, e);
                }
            });
        }
    }

    _updateCanvasControls() {
        // src: https://stackoverflow.com/a/63480476

        var fill = '#b2ccff'; // default color in hex - to avoid update on first render

        const rotateIcon = (color) => {
            if(color.charAt(0) === '#'){
                // # in svg needs to be escaped! https://stackoverflow.com/a/61099329
                color = '%23' + color.substring(1);
            }
            return "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' width='24' height='24'%3E%3Cpath d='M12 22C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10-4.477 10-10 10zm4.82-4.924a7 7 0 1 0-1.852 1.266l-.975-1.755A5 5 0 1 1 17 12h-3l2.82 5.076z' fill='"+color+"'/%3E%3C/svg%3E";
        }

        const drawIcon = (ctx, left, top, fabricObject, size) => {
            ctx.save();
            ctx.translate(left, top);
            ctx.rotate(fabric.util.degreesToRadians(fabricObject.angle));
            ctx.drawImage(img, -size / 2, -size / 2, size, size);
            ctx.restore();
        }

        var img = document.createElement('img');
        img.src = rotateIcon(fill);
        img.dataset.fill = fill; // set as data attribute to detect when fill changed

        // all defaults controls
        // https://github.com/fabricjs/fabric.js/blob/master/src/controls/default_controls.ts

        // custom control for rotation
        fabric.Object.prototype.controls.mtr = new fabric.Control({
            x: 0,
            y: -0.5,
            offsetY: -40,
            cursorStyle: 'crosshair',
            actionHandler: fabric.controlsUtils.rotationWithSnapping,
            actionName: 'rotate',
            render: function(ctx, left, top, styleOverride, fabricObject) {

                // we use the corner size set on object that is dynamic (we update it in layer-renderer based on touch support)
                const size = fabricObject.cornerSize * 1.5; // 1.5 to scale icon a bit bigger

                // create new image src if fill color is different
                if(img.dataset.fill != ctx.fillStyle){
                    // IMPORTANT: we have to re-draw the image when it's loaded to actually get the right color
                    img.onload = function(){
                        drawIcon(ctx, left, top, fabricObject, size);
                    };
                    img.dataset.fill = ctx.fillStyle;
                    img.src = rotateIcon(ctx.fillStyle);
                }

                drawIcon(ctx, left, top, false, size);
            },
            cornerSize: 28, // default
            withConnection: true
        });

    }

    _watchZoom() {

        // lower canvas zoom
        let canvasEl = this._canvas.getElement();

        // preview element
        let parent = canvasEl.parentElement;

        // INFO: we use the fabric enableRetinaScaling feature to render in higher quality to make the zoom look good
        if(this.settings.zoom.factor > fabric.devicePixelRatio){
            fabric.devicePixelRatio = Math.min(4, this.settings.zoom.factor);  // set retina scaling factor based on the zoom  (max 4x)
        }

        const applyZoom = (e) => {

            // init original dimensions to hide overflow
            if(!this.settings.zoomMode){

                // move style from canvas to parent
                parent.style.width = canvasEl.style.width;
                parent.style.height = canvasEl.style.height;

                // INFO: canvas width/height is multiplied by devicePixelRatio in fabric so we have to take it into account for the actual zoom target
                let pixelRatio = this._canvas.enableRetinaScaling ? fabric.devicePixelRatio : 1;

                let targetWidth = (canvasEl.width / pixelRatio) * this.settings.zoom.factor;
                let targetHeight = (canvasEl.height / pixelRatio) * this.settings.zoom.factor;
                let sourceWidth = parent.offsetWidth;
                let sourceHeight = parent.offsetHeight;

                // calculate zoom ratio and offset based on target (only calc once per zoom start)
                this._zoomCalc = {
                    xRatio: (parent.offsetWidth - targetWidth) / sourceWidth,
                    yRatio: (parent.offsetHeight - targetHeight) / sourceHeight,
                    offset: parent.getBoundingClientRect()
                };

                canvasEl.style.width = targetWidth + 'px';
                canvasEl.style.height = targetWidth + 'px';

                parent.classList.add('cl-po--zoom');

                this.settings.zoomMode = true;

                log.debug('----> zoom start', canvasEl, this._zoomCalc);
            }

            let left,top;
            if (e.type == 'touchstart' || e.type == 'touchmove') {
                let touch = e.touches[0] || e.changedTouches[0];
                left = touch.pageX - this._zoomCalc.offset.left;
                top = touch.pageY - this._zoomCalc.offset.top;

                // required to avoid scrolling and moving page on mobile
                e.preventDefault();
            } else {
                left = e.pageX - this._zoomCalc.offset.left;
                top = e.pageY - this._zoomCalc.offset.top;
            }

            canvasEl.style.left = (left * this._zoomCalc.xRatio) + 'px';
            canvasEl.style.top = (top * this._zoomCalc.yRatio) + 'px';
        };

        const resetZoom = (e) => {

            parent.classList.remove('cl-po--zoom');

            // reset parent style on canvas
            canvasEl.style.width = parent.style.width;
            canvasEl.style.height = parent.style.height;

            // remove parent styles
            parent.style.width = '';
            parent.style.height = '';

            canvasEl.style.left = '';
            canvasEl.style.top = '';

            this.settings.zoomMode = false;

            log.debug('----> zoom end', canvasEl);
        };

        canvasEl.addEventListener('mousemove', applyZoom);
        canvasEl.addEventListener('mouseleave', resetZoom);

        // mobile zoom support
        if(fabric.isTouchSupported && this.settings.zoom.mobile) {
            canvasEl.addEventListener('touchstart', applyZoom);
            canvasEl.addEventListener('touchmove', applyZoom);
            canvasEl.addEventListener('touchend', resetZoom);
        }

    }

    // ---- Layer Actions Tooltip ----

    /**
     * Initialize the layer actions tooltip with a virtual element for dynamic positioning
     * @param {Object} layer - The layer to create initial content for
     */
    _initLayerActionsTooltip(layer) {
        // Create the tooltip content
        const content = this._createLayerActionsContent(layer);

        // Create tippy instance with virtual element
        this._layerActionsTippy = tippy(document.createElement('div'), {
            content: content,
            allowHTML: true,
            interactive: true,
            theme: 'cl-actions',
            trigger: 'manual',
            placement: 'bottom',
            appendTo: document.body,
            offset: [0, 8],
            hideOnClick: false,
            zIndex: 9999,
            getReferenceClientRect: () => this._getLayerActionsRect(),
            onHide: () => {
                this._currentActionsLayer = null;
            }
        });
    }

    /**
     * Create the HTML content for the layer actions tooltip
     * @param {Object} layer - The layer to create actions for
     * @returns {HTMLElement}
     */
    _createLayerActionsContent(layer) {
        const container = document.createElement('div');
        container.className = 'cl-layer-actions';

        const actions = layer.params.interactiveActions;

        container.innerHTML = `
            ${(actions.edit && layer.params.crop && layer.type.includes('upload')) ? `
            <button type="button" class="cl-layer-action" data-action="crop" title="Crop">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18">
                    <path fill="currentColor" d="M15 17v2H6a1 1 0 0 1-1-1V7H2V5h3V2h2v15h8zm2 5V7H9V5h9a1 1 0 0 1 1 1v11h3v2h-3v3h-2z"/>
                </svg>
            </button>` : ''}
            ${actions.edit ? `
            <button type="button" class="cl-layer-action" data-action="edit" title="${layer.type.includes('upload') ? 'Replace image' : 'Edit'}">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18">
                    <path d="M15.7279 9.57627L14.3137 8.16206L5 17.4758V18.89H6.41421L15.7279 9.57627ZM17.1421 8.16206L18.5563 6.74785L17.1421 5.33363L15.7279 6.74785L17.1421 8.16206ZM7.24264 20.89H3V16.6473L16.435 3.21231C16.8256 2.82179 17.4587 2.82179 17.8492 3.21231L20.6777 6.04074C21.0682 6.43126 21.0682 7.06443 20.6777 7.45495L7.24264 20.89Z" fill="currentColor"/>
                </svg>
            </button>` : ''}
            ${actions.center ? `
            <button type="button" class="cl-layer-action" data-action="centerH" title="Center Horizontal">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18">
                    <path d="M11 2H13V7H21V10H13V14H18V17H13V22H11V17H6V14H11V10H3V7H11V2Z" fill="currentColor"/>
                </svg>
            </button>` : ''}
            ${actions.center ? `
            <button type="button" class="cl-layer-action" data-action="centerV" title="Center Vertical">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18">
                    <path d="M22 11V13H17V21H14V13H10V18H7V13H2V11H7V6H10V11H14V3H17V11H22Z" fill="currentColor"/>
                </svg>
            </button>` : ''}
            ${actions.index ? `
            <button type="button" class="cl-layer-action" data-action="indexDown" title="Send Backward">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18">
                <path d="M14 3C14.5523 3 15 3.44772 15 4V9H20C20.5523 9 21 9.44772 21 10V20C21 20.5523 20.5523 21 20 21H10C9.44772 21 9 20.5523 9 20V15H4C3.44772 15 3 14.5523 3 14V4C3 3.44772 3.44772 3 4 3H14ZM13 5H5V13H9V10C9 9.44772 9.44772 9 10 9H13V5Z" fill="currentColor"></path>
                </svg>
            </button>` : ''}
            ${actions.index ? `
            <button type="button" class="cl-layer-action" data-action="indexUp" title="Bring Forward">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18">
                <path d="M14 3C14.5523 3 15 3.44772 15 4V9H20C20.5523 9 21 9.44772 21 10V20C21 20.5523 20.5523 21 20 21H10C9.44772 21 9 20.5523 9 20V15H4C3.44772 15 3 14.5523 3 14V4C3 3.44772 3.44772 3 4 3H14ZM13 5H5V13H13V5Z" fill="currentColor"></path>
              </svg>
            </button>` : ''}
            ${actions.duplicate ? `
            <button type="button" class="cl-layer-action" data-action="duplicate" title="Duplicate">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18">
                    <path d="M6.9998 6V3C6.9998 2.44772 7.44752 2 7.9998 2H19.9998C20.5521 2 20.9998 2.44772 20.9998 3V17C20.9998 17.5523 20.5521 18 19.9998 18H16.9998V20.9991C16.9998 21.5519 16.5499 22 15.993 22H4.00666C3.45059 22 3 21.5554 3 20.9991L3.0026 7.00087C3.0027 6.44811 3.45264 6 4.00942 6H6.9998ZM5.00242 8L5.00019 20H14.9998V8H5.00242ZM8.9998 6H16.9998V16H18.9998V4H8.9998V6Z" fill="currentColor"/>
                </svg>
            </button>` : ''}
            ${actions.delete ? `
            <button type="button" class="cl-layer-action cl-layer-action-delete" data-action="delete" title="Delete">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18">
                    <path d="M17 6H22V8H20V21C20 21.5523 19.5523 22 19 22H5C4.44772 22 4 21.5523 4 21V8H2V6H7V3C7 2.44772 7.44772 2 8 2H16C16.5523 2 17 2.44772 17 3V6ZM18 8H6V20H18V8ZM9 11H11V17H9V11ZM13 11H15V17H13V11ZM9 4V6H15V4H9Z" fill="currentColor"/>
                </svg>
            </button>` : ''}
        `;

        // Attach event listeners
        container.querySelectorAll('.cl-layer-action').forEach(btn => {
            btn.addEventListener('click', (e) => {
                e.stopPropagation();
                this._handleLayerAction(btn.dataset.action);
            });
        });

        return container;
    }

    /**
     * Calculate the bounding rectangle for positioning the tooltip at the bottom-center
     * @returns {DOMRect}
     */
    _getLayerActionsRect() {
        if (!this._currentActionsLayer || !this._currentActionsLayer._object) {
            return { top: 0, left: 0, bottom: 0, right: 0, width: 0, height: 0 };
        }

        const object = this._currentActionsLayer._object;
        // When hasControls=false, oCoords aren't updated during drag, so we need to force recalculation
        const boundingRect = object.hasControls ? object.getBoundingRect() : object.getBoundingRect(false, true);
        const canvasEl = this._canvas.lowerCanvasEl;
        const canvasRect = canvasEl.getBoundingClientRect();

        // Calculate position relative to viewport (bottom-center of the layer + offset to avoid interactive corners)
        let left = canvasRect.left + boundingRect.left + (boundingRect.width / 2);
        let top = canvasRect.top + boundingRect.top + boundingRect.height + 4; // 4px offset below the layer for corners

        // Clamp to canvas bounds
        // Note: with bottom placement, tooltip extends equally LEFT and RIGHT from anchor,
        // so we need half tooltip width margin on each side
        const tooltipHalfWidth = 60;
        const tooltipHeight = 40;
        left = Math.max(canvasRect.left + tooltipHalfWidth, Math.min(left, canvasRect.right - tooltipHalfWidth));
        top = Math.max(canvasRect.top, Math.min(top, canvasRect.bottom - tooltipHeight));

        return {
            top: top,
            left: left,
            bottom: top,
            right: left,
            width: 0,
            height: 0
        };
    }

    _showLayerActionsTooltip(layer) {
        // Check if layer has at least one interactive action enabled and preview is active.
        const config = layer?.params?.interactiveActions;
        const hasEnabledAction = config && Object.values(config).some(Boolean);
        if (!hasEnabledAction || !this.active) {
            this._hideLayerActionsTooltip();
            return;
        }

        // Don't show during crop mode
        if (this.settings.cropMode) {
            return;
        }

        // Store current layer reference
        this._currentActionsLayer = layer;

        // Initialize or update tooltip
        if (!this._layerActionsTippy) {
            this._initLayerActionsTooltip(layer);
        } else {
            // Update content for new layer (may have different actions)
            const newContent = this._createLayerActionsContent(layer);
            this._layerActionsTippy.setContent(newContent);
        }

        // Show the tooltip
        this._layerActionsTippy.show();
    }

    _hideLayerActionsTooltip() {
        if (this._layerActionsTippy) {
            this._layerActionsTippy.hide();
        }
        this._currentActionsLayer = null;
    }


    _updateLayerActionsPosition() {
        if (this._layerActionsTippy && this._layerActionsTippy.state.isVisible) {
            // Force tippy to recalculate position by calling popperInstance.update()
            this._layerActionsTippy.popperInstance?.update();
        }
    }

    /**
     * Handle layer action button clicks
     * @param {string} action - The action to perform ('edit', 'duplicate', 'delete')
     */
    _handleLayerAction(action) {
        const layer = this._currentActionsLayer;
        if (!layer) return;

        if (action === 'crop') {
            if (layer.params.crop && layer.type.includes('upload')) {
                const option = RenderUtil.getProductOption(layer.params.option, this.config.options);
                if (option) {
                    this._hideLayerActionsTooltip();
                    this._optionCrop({ detail: option }, layer);
                }
            }
            return;
        }

        if (action === 'indexUp' || action === 'indexDown') {
            this.moveLayer(layer, action === 'indexUp' ? 'up' : 'down');
            return;
        }

        // Emit custom event for external handling
        this._triggerEvent(`layer:action:${action}`, layer);

        // For delete and duplicate, hide tooltip immediately
        if (action === 'delete' || action === 'duplicate') {
            this._hideLayerActionsTooltip();
        }
    }

    /**
     * Center a layer horizontally or vertically within its reference area
     * @param {Object} layer - The layer to center
     * @param {string} direction - 'horizontal' or 'vertical'
     */
    _centerLayer(layer, direction) {
        if (!layer?._object) return;

        const context = this._renderContext();
        const refDimensions = RenderUtil.getRefDimensions(layer, context);
        const refCoordinates = RenderUtil.getRefCoordinates(layer, context);
        const object = layer._object;

        if (direction === 'horizontal') {
            // Center horizontally: object center X = ref center X
            const centerX = refCoordinates.x + (refDimensions.width / 2);
            const objectWidth = object.getScaledWidth();
            object.set('left', centerX - (objectWidth / 2));
            object.setCoords();
        } else {
            // Center vertically: object center Y = ref center Y
            const centerY = refCoordinates.y + (refDimensions.height / 2);
            const objectHeight = object.getScaledHeight();
            object.set('top', centerY - (objectHeight / 2));
            object.setCoords();
        }

        // Store position in layer params for persistence
        layer.params.x = object.left;
        layer.params.y = object.top;

        // persist the current scale as intent (mirrors object:modified) - _interactiveScaled disables
        // the autoScale reset, so without params.scaleX nothing re-asserts the scale on re-render and
        // a refArea fitScale projection would otherwise stick on the cached object
        layer.params.scaleX = parseFloat(object.scaleX.toFixed(3));
        layer.params.scaleY = parseFloat(object.scaleY.toFixed(3));

        // Text editor params override layer.params during render (text-renderer extends _editor over
        // params), so keep the editor cache in sync - otherwise the pre-center position wins on the
        // next full re-render (e.g. a text change) and the object snaps back. Mirrors object:modified.
        if(layer._editor){
            Util.merge(layer._editor, layer.params, ['x','y','scaleX','scaleY','angle']);
        }

        layer._interactive = true;
        layer._interactiveScaled = true;

        // Re-render canvas
        this._canvas.renderAll();

        // Trigger change event for persistence
        this._triggerEvent('layer:centered', layer);
    }

    _destroyLayerActionsTooltip() {
        if (this._layerActionsTippy) {
            this._layerActionsTippy.destroy();
            this._layerActionsTippy = null;
        }
        this._currentActionsLayer = null;
    }
}

export default ProductPreview;
